import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {SOCKET_ACTION, SOCKET_EVENT_TYPE} from '../../constants';
import {EVENT_ID, subscribe, unsubscribe} from '../../containers/event_manager';
import Util from '../../forecast-app/shared/util/util';
import {
	LoadedReport,
	LockClaim,
	LockStatus,
	ReportDataSource,
	ReportDraftState,
	ReportExportTooLargeError,
	ReportLifecycleState,
	isReportDataAccessDenied,
} from './data/ReportDataSource';
import {SavedReportRef} from './data/savedReportInput';
import {ReportRow} from './data/reportRowModel';
import {Condition, ReportDefinitionV1} from './contracts/report-definition.types';
import {grainOf} from './reportGrain';
import {AUDIT_LOG_TAXONOMY_TYPE} from './auditLogReport';
import {trackAuditLogReportSearched} from '../../tracking/amplitude/timeApprovalEvents';
import {auditLogSearchReport} from '../../tracking/amplitude/auditLogSearchSignature';
import {grainRootOf, TASK_GRAIN_ROOT} from './data/definitionFields';
import {fetchFieldsFor} from './data/HttpReportDataSource';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {defaultOperatorForDataType, operatorForValues} from './conditions/conditionOperators';
import {DatePresetToken, findRelativeDatePreset} from './relativeDatePresets';
import {DateScope, effectiveReportPeriod, resolveDatePeriod, withPeriodFilterConditions} from './periodFilter';
import {ReportTableState} from './ReportTable/ReportTable';
import {GroupingToken} from './ReportTable/useClientGrouping';
import {
	EXPANDER_COLUMN_ID,
	FAVORITE_COLUMN_ID,
	FIXED_COLUMN_IDS,
	ensureFieldPath,
	flattenFieldPaths,
	orderedDataColumnIds,
} from './ReportTable/useReportTableColumns';
import {useLockHeartbeat} from './useLockHeartbeat';
import {useViewerLayoutOverlay} from './useViewerLayoutOverlay';

/** 'unlocked' free; 'held' this tab holds it; 'blocked' another user holds it; 'lost' this tab held
 * it and no longer does (it lapsed and someone else has since claimed it), so edits cannot
 * be saved but are not discarded. */
export type LockState = 'unlocked' | 'held' | 'blocked' | 'lost';

export type BuilderMode = 'edit' | 'view';
export type BuilderRole = 'editor' | 'reader';

/** What `publish()` resolves with. 'unconfirmed' is deliberately neither success nor failure: the
 * bounded wait expired, so the publish may still be in flight server-side. 'locked' covers every
 * reason this tab does not hold the edit lock, whether it never had it or lost the one it held —
 * both leave the working copy untouched, and both are explained by the same copy. 'stale' is its own
 * outcome, not a flavour of 'locked': this tab may well hold the lock now, and the problem is that the
 * working copy on screen predates what another editor stored, so publishing it would overwrite their
 * work. The remedy is different too — reload or discard, not "try again once they are done". */
export type PublishOutcome = 'published' | 'locked' | 'failed' | 'unconfirmed' | 'stale';
/** What `discard()` resolves with. 'reload_failed' means the draft WAS dropped but the reverted
 * definition could not be read back; 'unconfirmed' means the bounded wait expired, so whether
 * anything was dropped is unknown; 'locked' means this tab does not hold the edit lock, so nothing was
 * dropped. */
export type DiscardOutcome = 'discarded' | 'locked' | 'failed' | 'reload_failed' | 'unconfirmed';

/** What `exportCsv()` resolves with. 'too_large' is separated from 'failed' because report-service
 * refuses an over-ceiling ordered export rather than truncating it, and the same request will be
 * refused again — so the surface has to say to narrow the report, not to try again. */
export type ExportOutcome = 'exported' | 'too_large' | 'failed';

export interface ReportIntentPayload {
	reportType?: string;
	suggestedName?: string;
	definition: ReportDefinitionV1;
	summaries?: unknown[];
	flags?: unknown[];
	draftId?: string | null;
	// Original natural-language prompt the report was generated from. Rendered
	// (quoted, italic) in the AI banner. Two producers: the agent populates it on the
	// create_report_intent event for a fresh draft, and re-opening a saved report populates it from
	// the stored prompt. Optional either way; when absent the banner falls back to the static
	// "Generated from the prompt".
	prompt?: string;
	// Which surface authored the report. Absent on the fresh-intent path, where the builder is the
	// only possible author.
	source?: 'AI' | 'STANDARD';
	// Lifecycle state the report arrived with. Present on the re-open path (read from the stored
	// definition), absent on the fresh-intent path, where nothing is saved yet.
	draftState?: ReportDraftState;
	publishedAt?: string | null;
	// Whether the draft this report arrived with (if any) would revert the filter on discard. Same
	// two-path reasoning as `draftState`/`publishedAt`: present on the re-open path, absent on the
	// fresh-intent path, where there is no draft to describe.
	draftFilterWillRevert?: boolean;
	// Whether the report has already been hand-edited since it was generated from its prompt. Present
	// on the re-open path (read from the stored definition), absent on the fresh-intent path, where
	// nothing has been edited yet.
	manuallyEdited?: boolean;
}

export interface ReportBuilderContextValue {
	definition: ReportDefinitionV1; // working draft
	prompt?: string; // original NL prompt (AI banner); undefined when the payload omits it
	// Which surface authored the report, resolved. Drives the provenance banner: a report the builder
	// generated says so, a report opened from anywhere else does not.
	source: 'AI' | 'STANDARD';
	// Whether the report has been hand-edited since it was generated from its prompt, combining what
	// storage reported with anything edited this session. Drives the AI banner's second state.
	manuallyEdited: boolean;
	mode: BuilderMode;
	role: BuilderRole;
	dirty: boolean;
	tableState: ReportTableState;
	grouping: GroupingToken[];
	taxonomy: ReportTaxonomy | null;
	setMode(m: BuilderMode): void;
	setName(name: string): void;
	// The in-progress rename text, staged separately from `definition.name` until it is committed.
	// Null while the rename field is closed; `ReportPageHeader` opens it by seeding this from
	// `definition.name` and clears it (without committing) on revert/Escape.
	pendingName: string | null;
	setPendingName(name: string | null): void;
	// Applies the trimmed pending name via `setName` when it is non-empty and different from the
	// stored name (an unchanged name must not mark the report dirty), then closes the field either
	// way. A blank or whitespace-only pending name is discarded rather than committed — falling back
	// to the stored name, because a blank one would fail the `nameValid` guard that gates Publish.
	// Returns the name now in effect, so a caller that commits and immediately needs the current name
	// (the publish toast, an export filename) is not stuck reading this render's stale `definition`
	// while React catches up. Safe to call at any time, including with nothing staged (a no-op that
	// returns `definition.name`) — `save()` and `exportCsv()` call it themselves so a caller of either
	// never has to remember to, and `ReportPageHeader`'s own submit/Enter path goes through this exact
	// function rather than re-implementing the same rule.
	commitPendingName(): string;
	// The reporting period, as the date-range control shows it. Null members mean that end is open.
	// datePreset is the relative token the period was resolved from, or null for a custom/absolute
	// range set via the date picker directly. dateScope is which date field(s) the range filters on;
	// null means `all_dates` - the default, and what every report saved before the scope selector
	// existed means.
	period: {
		startDate: string | null;
		endDate: string | null;
		datePreset: DatePresetToken | null;
		dateScope: DateScope | null;
	};
	// Replaces the period with an absolute range and clears any selected preset. Rewrites the
	// definition's date filter, so the table re-runs for the new window; an open end drops that side
	// of the filter rather than filtering on a missing date.
	setPeriod(startDate: string | null, endDate: string | null): void;
	// Resolves a relative preset against the current date and replaces the period with the result,
	// recording the token so the period is recomputed fresh on every future load.
	setPeriodPreset(token: DatePresetToken): void;
	// Changes which date field(s) the period filters on, keeping the range itself unchanged. Rewrites
	// the definition's date filter to match the new scope.
	setPeriodScope(scope: DateScope): void;
	setGrouping(g: GroupingToken[]): void;
	updateTableState(patch: Partial<ReportTableState>): void;
	// Serialises layout/sort/grouping into the definition and persists it into the Reports list via
	// `dataSource.saveDefinition` — creating the report on the first save and updating the same
	// report on every save after it. Resolves `true` when the report is saved and `false` when the
	// save failed; it never rejects, so a caller that forgets to handle failure gets a no-op rather
	// than an unhandled rejection that tears the surface down. A failed save leaves the draft dirty
	// and editable, which keeps Save enabled as the standing "not saved yet" signal.
	save(): Promise<boolean>;
	// Id of the Reports-list entry this draft has been saved as, or null while it is unsaved. Set by
	// the first successful `save()` and reused to update that entry rather than create duplicates.
	// Deliberately NOT the signal for "does this report exist in storage" — a stored report whose
	// sharing flag came back unrecognised is seeded with no `savedReportSeed` (see
	// `ReportBuilderPage`) so a save cannot guess and flip its privacy, which leaves this null even
	// though the report is genuinely saved. Use `openedFromStorage` for that question instead.
	savedReportId: string | null;
	// Whether the builder was opened on a report that already exists in storage (the route carried a
	// report id), as opposed to a fresh chat-generated draft that exists only in this session. Unlike
	// `savedReportId`, this stays true regardless of whether the stored record's sharing flag was
	// readable, because it answers a different question: not "can a save target this record without
	// guessing," but "would leaving right now destroy something with no copy anywhere else."
	openedFromStorage: boolean;
	// Records a privacy change made outside `save()` — the share dialog's "share with company" toggle
	// commits straight to the backend, so without this the next `save()` would echo the stale value it
	// mounted with (see `buildUpdateSavedReportInput`) and silently re-privatize a report the user just
	// shared. A no-op while there is no saved report yet: an unsaved draft cannot be shared, so the
	// dialog is unreachable there.
	updateSavedReportPrivateAccess(privateAccess: boolean): void;
	// True while a save is in flight. The surface must not allow a second save while set: a repeated
	// first save would create a second Reports-list entry for the same draft.
	saving: boolean;
	// Exports the current definition/grouping's full (unpaginated) result set as a CSV via
	// `dataSource.exportCsv`. The definition sent is the draft with the live table state folded in (the
	// same serialisation `save()` performs, without persisting it), so the CSV's column order and row
	// order match the table on screen even when the session's reorder/resize/sort has not been saved.
	// Resolves `'exported'` when the file was handed to the browser, `'too_large'` when the report has
	// more rows than the export will order, and `'failed'` for anything else. `'too_large'` is its own
	// outcome because it is the one failure retrying cannot fix, so the surface must not offer that as
	// the remedy. Never rejects, mirroring `save()`'s contract for the same reason — a caller that
	// forgets to handle failure gets a no-op rather than an unhandled rejection that tears the surface
	// down.
	exportCsv(): Promise<ExportOutcome>;
	// True while an export is in flight. The surface must not allow a second export while set: a
	// repeated click would start a second full-dataset export against the same definition.
	exporting: boolean;
	// Whether the report has a usable name. The name an unsaved draft arrives with is the agent's
	// suggestion, which is an acceptable name to save under; only a blank one blocks Save, because a
	// nameless entry in the Reports list cannot be identified or reopened.
	nameValid: boolean;
	toggleFavorite(rowId: string): void; // optimistically flips the row, persists via the data source, reverts on failure
	addCondition(fieldKey: string): void; // no-op when taxonomy is null or fieldKey is unknown
	// Adds a taxonomy column to the definition's `fields`, which is what makes it a real column:
	// fetched, rendered, listed in ColumnManager and saved. No-op when taxonomy is null, when
	// fieldKey is unknown, or when the column is already there.
	addColumn(fieldKey: string): void;
	// `operator` is only supplied by a control that writes the whole comparison itself (Remaining
	// work's Yes/No). Every other picker omits it, and the operator keeps being inferred from how
	// many values were picked.
	updateConditionValues(index: number, values: string[], operator?: string): void;
	removeCondition(index: number): void;
	rows: ReportRow[];
	totalCount: number;
	capped: boolean;
	cappedTotalEstimate: number;
	totalIsFloor: boolean;
	// False until the first fetch for the current definition resolves. Lets the surface tell
	// "not fetched yet" apart from "fetched, zero matches" so the empty state never claims the
	// report ran while the initial fetch is still in flight.
	hasFetched: boolean;
	// True when the fetch rejected (non-2xx or network failure). Kept separate from `hasFetched`
	// so the surface can show an error instead of the empty state — the empty state must only ever
	// claim "the report ran and matched nothing" after a real success.
	fetchFailed: boolean;
	// True when the failure behind `fetchFailed` was the server refusing this viewer the data, rather
	// than something a retry could clear. Always false while `fetchFailed` is false. Lets the surface
	// say so plainly and drop the retry, which on a refusal can only fail again.
	fetchAccessDenied: boolean;
	// Re-runs the fetch for the current definition. The fetch is otherwise only triggered by a
	// definition change, so without this a transient network failure would be a dead end.
	retryFetch(): void;
	// Where the report sits in the publish lifecycle. 'UNPUBLISHED' until a lifecycle call or a load
	// says otherwise, e.g. a fresh AI-generated draft that has never been saved.
	draftState: ReportDraftState;
	publishedAt: string | null;
	// Whether discarding the currently open draft would also revert the report's filter. False
	// whenever there is no open draft, or the draft predates this field. Drives whether the discard
	// confirmation copy warns about the filter.
	draftFilterWillRevert: boolean;
	// True while a publish is in flight, mirroring `saving`'s role for the reentrancy check and for
	// the surface to disable the Publish action.
	publishing: boolean;
	// True while a discard is in flight, same reasoning as `publishing`.
	discarding: boolean;
	// True when what is on screen is no longer known to match the stored report, so nothing on screen is
	// safe to publish. Set when a discard dropped the draft but its re-read failed (the definition on
	// screen is the copy that was just discarded), and when a lifecycle call's bounded wait expired (the
	// call may still land server-side, so what is stored is unknown). The surface must gate Publish and
	// Discard on this and say so, rather than offering to publish content it cannot vouch for, and must
	// tell the editor to reload the page — that is the recovery, because only a fresh load can be trusted
	// to show what is stored. Cleared only by a re-read that succeeds within the same session (the
	// discard's own re-read). Deliberately NOT cleared by an edit: an edit on top of a stale base is
	// still stale.
	reloadRequired: boolean;
	// True when another user took the edit lock off this tab mid-edit, so what is on screen may no longer
	// match the stored report: the new holder can save and publish while they have it, and this tab sees
	// none of that. The unsaved working copy stays on screen either way — that is the guarantee.
	//
	// It gates `publish()` (which resolves 'stale' rather than writing) and nothing else: publishing a copy
	// that predates what the other holder stored silently destroys their work, which is the one thing that
	// cannot be undone from here. `save()` is deliberately NOT gated — a save writes the draft only, leaves
	// the published version intact, and is the way unsaved work gets parked; nor is `discard()`, which drops
	// the stale draft and re-reads what is stored, and is one of the two ways out.
	//
	// It is NOT `reloadRequired`, which only a page reload ever clears: that stranded a tab which went on to
	// re-acquire the lock, so with one tab displaced and one holding, nobody could publish at all. Cleared by
	// a successful save (this tab's copy is now what is stored), by a discard's re-read, by leaving edit mode,
	// and by re-entering edit or re-claiming the lock — which READS the stored definition and clears this
	// whenever it still matches what this tab knew was stored. Holding a lock is not editing, so a takeover
	// that stored nothing must not leave an editor unable to publish: it stays set only while the stored
	// definition genuinely differs, and while it does, unsaved work is kept on screen rather than replaced.
	definitionMayBeStale: boolean;
	// True when an editor this tab was BLOCKED on ended their session, so the stored report may have moved
	// on from what is rendered here. Distinct from `definitionMayBeStale`, which is about THIS tab having
	// lost a lock it held and so speaks to unsaved work on screen; this one belongs to a tab that never
	// held anything and has nothing unsaved — it only needs to know that reading on may mean reading a
	// superseded report. Set on the lock going free from 'blocked', and cleared wherever the view and
	// storage are known to agree again — a re-read on entering edit, a discard, or a save — so it does not
	// outlive the doubt it describes.
	storedReportMayHaveChanged: boolean;
	// True when a publish got as far as saving the working copy but never landed the publish itself (the
	// publish call failed, or another user held the edit lock). The draft is genuinely saved, so `dirty`
	// is false and `draftState` is unchanged, which would otherwise leave the Publish action disabled
	// with no way back other than making an unrelated edit. The surface must include this in whatever
	// enables Publish. Cleared by the next successful publish, and by a discard, which throws the
	// working copy away so there is nothing left to publish.
	publishOwed: boolean;
	// Where this tab stands on the edit lock. See `LockState`. Read by the banner and by the Edit
	// button's disabled state.
	lockState: LockState;
	// Who holds the lock right now, when it is held by someone else ('blocked') or was ('lost'/held
	// before a takeover named a new holder). Null while 'unlocked' or 'held' by this tab.
	lockHolder: LockStatus['holder'];
	// When the current holder's session started, alongside `lockHolder`. Null under the same
	// conditions `lockHolder` is.
	lockedAt: string | null;
	// Opens the edit session before handing the surface over to edit mode. Never rejects: a draft call
	// that fails still enters edit mode, because the alternative is an editor stuck in view mode. A draft
	// call whose bounded wait expired enters edit mode with `reloadRequired` set, because whether a draft
	// is open is then unknown.
	//
	// The edit lock is acquired FIRST, and entering edit mode is now conditional on that: report-service
	// does not lock-check draft entry itself, so this is the only gate. A contended lock sets `lockState`
	// to 'blocked', records the holder, and returns WITHOUT opening a draft or touching `mode` — the
	// alternative (opening a draft anyway) would let a blocked editor's local edits diverge from the
	// session the other editor actually holds.
	beginEdit(): Promise<void>;
	// Saves the working copy and makes it the version readers execute. Resolves 'published' on
	// success, 'locked' whenever this tab does not hold the edit lock (another user holds it, or this tab
	// lost the one it held), 'stale' when the working copy is not known to match the stored report
	// (`definitionMayBeStale`), 'failed' for a failed save or publish,
	// and 'unconfirmed' when the call did not settle inside `LIFECYCLE_TIMEOUT_MS` — which is neither
	// success nor failure, because the request may still land server-side. Never rejects, for the same
	// reason `save()` does not.
	publish(): Promise<PublishOutcome>;
	// Drops the edit session and adopts the definition report-service reverted to. Resolves
	// 'discarded', 'locked', 'failed' (nothing was discarded, nothing changed), 'reload_failed' (the
	// draft WAS discarded, but the reverted definition could not be read back, so what is on screen is
	// stale) or 'unconfirmed' (the call did not settle in time, so whether anything was discarded is
	// unknown). Never rejects.
	discard(): Promise<DiscardOutcome>;
}

const ReportBuilderContext = createContext<ReportBuilderContextValue | null>(null);

/** Collapse window for definition-driven refetches. Long enough that ticking several values in a
 * condition's picker issues one request instead of one per click, short enough that a single edit still
 * feels immediate next to the request's own latency. */
const FETCH_DEBOUNCE_MS = 400;

/** Bound on how long any single lifecycle round trip is waited on: entering a draft, publishing,
 * discarding, and the definition re-reads that follow a discard.
 *
 * What it respects: the slowest legitimate round trip. Each of those calls is one short report-service
 * transaction (publish writes a snapshot row for the working copy, discard clears the draft pointer,
 * entering a draft records the session), reached through the graphql hop, so even a loaded environment
 * with a cold connection pool settles in low single-digit seconds. The bound is set an order of
 * magnitude above that rather than close to it on purpose.
 *
 * What exceeding it costs: the editor is told the outcome could not be confirmed and has to reload the
 * page to find out where it landed — so a merely slow but successful publish must never trip it, which
 * is why the headroom is generous. The bound exists because without one a request that never settles (a
 * hung request, or a dropped connection with no browser-level timeout) latches the shared in-flight guard
 * for the rest of the session: Publish, Discard and the edit-session call are all refused from then on,
 * with the buttons disabled and no explanation. */
export const LIFECYCLE_TIMEOUT_MS = 30000;

/** How many times the automatic claim re-tries an acquire that failed, and how long it waits between
 * tries. Bounded on purpose: a claim that keeps failing is a report this tab cannot edit safely, and
 * retrying forever would put a request on the wire for the rest of the session. Spaced on purpose too —
 * an immediate retry off the back of a rejection is a spin, not a retry. */
export const LOCK_CLAIM_RETRY_LIMIT = 3;
export const LOCK_CLAIM_RETRY_DELAY_MS = 5000;

/** A bounded wait's result: the settled value, or the fact that the wait expired first. */
type BoundedResult<T> = {timedOut: true} | {timedOut: false; value: T};

/** Races `work` against `LIFECYCLE_TIMEOUT_MS`. A rejection propagates, so callers keep handling
 * transport failures in their own catch; expiry resolves `{timedOut: true}` instead, because a call
 * that has not settled is not a call that failed. The pending work is given its own no-op catch: after
 * an expiry nothing awaits it any more, and a late rejection would otherwise surface as an unhandled
 * rejection. The timer is always cleared, so an expired-or-settled call leaves no pending timeout
 * behind. */
function withLifecycleTimeout<T>(work: Promise<T>): Promise<BoundedResult<T>> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const settled: Promise<BoundedResult<T>> = work.then(value => ({timedOut: false as const, value}));
	settled.catch(() => undefined);
	const expiry = new Promise<BoundedResult<T>>(resolve => {
		timer = setTimeout(() => resolve({timedOut: true}), LIFECYCLE_TIMEOUT_MS);
	});
	return Promise.race([settled, expiry]).finally(() => {
		if (timer !== undefined) {
			clearTimeout(timer);
		}
	});
}

/** The period the date control should show. Prefers the definition's own dates and falls back to the
 * date filter, so a report saved before the period was editable - or scoped to a single own-date
 * field rather than all dates - still shows the window it filters on. */
function periodOf(definition: ReportDefinitionV1): {
	startDate: string | null;
	endDate: string | null;
	datePreset: DatePresetToken | null;
	dateScope: DateScope | null;
} {
	// The two date fields come from `effectiveReportPeriod`, which the fetch body also uses, so the
	// range a reader is SHOWN and the range report-service is TOLD to report against cannot drift
	// apart. They did: the fetch sent no range at all, so every row's time-dependent value described
	// today rather than the month in the header.
	return {
		...effectiveReportPeriod(definition),
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
	};
}

/** Canonical `columnOrder` derived from the definition alone (no overlay/table-state input):
 * layout order when present, else field-tree order, wrapped by the fixed expander/favourite
 * columns — reuses `useReportTableColumns`'s `orderedDataColumnIds` so the initial table state
 * matches what the columns hook will actually render (expander leading, favourite column
 * trailing — see `useReportTableColumns`). */
function canonicalColumnOrder(definition: ReportDefinitionV1): string[] {
	return [EXPANDER_COLUMN_ID, ...orderedDataColumnIds(definition), FAVORITE_COLUMN_ID];
}

function canonicalColumnSizing(definition: ReportDefinitionV1): Record<string, number> {
	const sizing: Record<string, number> = {};
	for (const entry of definition.layout ?? []) {
		// A width that was never set is absent on an agent-generated definition but null on one read back
		// from a saved report (the stored column width is nullable), so both have to be treated as unset —
		// a null here would reach the table as a null column size.
		if (typeof entry.width === 'number') {
			sizing[entry.field] = entry.width;
		}
	}
	return sizing;
}

function canonicalSorting(definition: ReportDefinitionV1): ReportTableState['sorting'] {
	if (!definition.sort) {
		return [];
	}
	return [{id: definition.sort.field, desc: definition.sort.direction === 'DESC'}];
}

/** Whether two table states describe the same arrangement. Compared by value rather than identity
 * because the working state is rebuilt on every interaction, so an identity check would report a
 * difference the moment anything was touched, even if it was put straight back. */
function isSameTableState(a: ReportTableState, b: ReportTableState): boolean {
	return (
		JSON.stringify(a.sorting) === JSON.stringify(b.sorting) &&
		JSON.stringify(a.columnOrder) === JSON.stringify(b.columnOrder) &&
		JSON.stringify(a.columnSizing) === JSON.stringify(b.columnSizing) &&
		JSON.stringify(a.columnVisibility) === JSON.stringify(b.columnVisibility)
	);
}

/** Initial `columnVisibility`, derived from `layout` only when the caller knows this builder wrote that
 * layout — `layoutRecordsVisibility`. `serializeDefinition` rewrites `layout` from the visible columns
 * on every save, so on a definition the builder itself stored a leaf field missing from a non-empty
 * layout IS a record that the user hid it, and it starts hidden. Only `false` means hidden (see
 * `hiddenColumnPaths`), so visible fields are simply absent from the map.
 *
 * A layout written by anything else says nothing about visibility. A freshly generated definition's
 * layout is the generator's column choice, commonly already a strict subset of its field set, and
 * treating that as hiding would silently drop columns the report has always rendered — so with
 * `layoutRecordsVisibility` false everything starts visible. A definition with no layout, or an empty
 * one, likewise carries no visibility information. */
function canonicalColumnVisibility(definition: ReportDefinitionV1, layoutRecordsVisibility: boolean): Record<string, boolean> {
	const layout = definition.layout;
	if (!layoutRecordsVisibility || !layout || layout.length === 0) {
		return {};
	}
	const inLayout = new Set(layout.map(entry => entry.field));
	const visibility: Record<string, boolean> = {};
	for (const path of flattenFieldPaths(definition.fields)) {
		if (!inLayout.has(path)) {
			visibility[path] = false;
		}
	}
	return visibility;
}

/** Table state derived from the definition alone, for the initial mount and for a post-discard revert.
 *
 * `layoutRecordsVisibility` must be true only when `definition` came back from storage — a re-opened
 * saved report, or the definition re-read after a discard. The definition cannot tell you who wrote its
 * layout, so provenance has to be threaded in from the caller; see `canonicalColumnVisibility`. */
function canonicalTableState(definition: ReportDefinitionV1, layoutRecordsVisibility: boolean): ReportTableState {
	return {
		sorting: canonicalSorting(definition),
		columnOrder: canonicalColumnOrder(definition),
		columnSizing: canonicalColumnSizing(definition),
		columnVisibility: canonicalColumnVisibility(definition, layoutRecordsVisibility),
	};
}

/** The data-column ids the user has switched off in ColumnManager. `columnVisibility` records only the
 * columns that have been toggled, and only `false` means hidden, so an absent entry is visible. */
function hiddenColumnPaths(tableState: ReportTableState): string[] {
	return Object.entries(tableState.columnVisibility)
		.filter(([, visible]) => visible === false)
		.map(([columnId]) => columnId);
}

/** Serializes the current `tableState`/`grouping` into a NEW definition object (never mutates
 * `base`), per spec §6 save() rules: `layout` is `columnOrder` filtered of fixed (`__`-prefixed)
 * and hidden columns, paired with `columnSizing` when set; `sort` is the primary sort column, if
 * any; `grouping` is copied verbatim.
 *
 * Dropping hidden columns from `layout` is exactly how hidden-ness is persisted — no visibility concept
 * on the definition contract is needed. Because every save rewrites `layout` this way, a leaf field
 * missing from the layout of a definition READ BACK FROM STORAGE is read as hidden by
 * `canonicalColumnVisibility`, while `orderedDataColumnIds` keeps it in the column model so ColumnManager
 * can switch it back on. That inference is only sound for a stored definition: it is not applied to a
 * freshly generated one, whose subset layout is the generator's column choice and not a user's hiding. */
function serializeDefinition(
	base: ReportDefinitionV1,
	tableState: ReportTableState,
	grouping: GroupingToken[]
): ReportDefinitionV1 {
	const layout = tableState.columnOrder
		.filter(id => !id.startsWith('__'))
		.filter(id => tableState.columnVisibility[id] !== false)
		.map(id => {
			const width = tableState.columnSizing[id];
			return width !== undefined ? {field: id, width} : {field: id};
		});

	const primarySort = tableState.sorting[0];
	const sort = primarySort
		? {field: primarySort.id, direction: (primarySort.desc ? 'DESC' : 'ASC') as 'ASC' | 'DESC'}
		: undefined;

	return {
		...base,
		layout,
		sort,
		grouping: grouping.slice() as ReportDefinitionV1['grouping'],
	};
}

/** Strips a value down to its content: object keys sorted at every level (and emitted as pairs, so no
 * later re-insertion can reorder them), keys whose value is null/undefined dropped, and empty lists
 * dropped along with them. */
function canonicalValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(canonicalValue);
	}
	if (typeof value !== 'object' || value === null) {
		return value;
	}
	return Object.entries(value as Record<string, unknown>)
		.filter(([, entry]) => entry !== null && entry !== undefined && !(Array.isArray(entry) && entry.length === 0))
		.sort(([left], [right]) => (left < right ? -1 : 1))
		.map(([key, entry]) => [key, canonicalValue(entry)]);
}

/** A stable string for a report definition's CONTENT, for deciding whether the stored report actually
 * changed while another user held the edit lock.
 *
 * The two definitions compared describe the same contract but do not arrive by the same route: one is a
 * definition read back from storage, the other is either an earlier such read or the copy this tab last
 * serialised into a save. Those routes agree on content and differ on presentation — each builds its
 * object in its own key order, a value the contract treats as absent is present as null on one side and
 * missing on the other, and an empty list on one side is no list at all on the other. Raw `JSON.stringify`
 * reports every one of those as a change, and a false "changed" is the worst answer available here: it
 * withholds publishing over a difference that does not exist, which strands unsaved work with nowhere to
 * go. Normalising both sides the same way is what makes the comparison worth trusting. */
function definitionSignature(definition: ReportDefinitionV1): string {
	return JSON.stringify(canonicalValue(definition));
}

export interface ReportBuilderProviderProps {
	payload: ReportIntentPayload;
	role?: BuilderRole;
	// Required: callers pass the transport explicitly (production: HttpReportDataSource via
	// ReportBuilderPage; tests/Storybook: a mock). No implicit default, so a mock can never be the
	// silent production fallback.
	dataSource: ReportDataSource;
	// Scopes the per-user localStorage layout overlay to the logged-in person. The page passes the
	// viewer's person id from the app's auth context; falls back to 'me' only when the id is unknown
	// (e.g. tests/Storybook that don't supply one).
	userId?: string;
	// Field metadata driving addCondition/updateConditionValues; null until the taxonomy for this
	// report type has loaded. addCondition no-ops while null.
	taxonomy?: ReportTaxonomy | null;
	/** The Reports-list entry this report already exists as, when the builder was opened on a saved
	 * report rather than a fresh draft. Without it the provider treats a saved report as a new draft
	 * and a save creates a duplicate entry instead of updating the one being edited. */
	savedReportSeed?: SavedReportRef;
	/** Whether the builder was opened on a report that already exists in storage (the route carried a
	 * report id), independent of whether `savedReportSeed` could be populated. Passed through as
	 * `openedFromStorage` on the context; see that field for why it is kept separate from
	 * `savedReportId`. Defaults to false: the only other way to reach the builder is the chat intent,
	 * which never has a stored record yet. */
	openedFromStorage?: boolean;
	/** Announces the report id the first time this builder turns its payload into a stored report.
	 * Fired once, on the transition from unsaved to saved — never on subsequent updates, where the id
	 * has not changed. Lets whatever proposed this report (today: a chat card) point at the stored
	 * report from then on instead of re-opening its own increasingly stale payload. Absent when the
	 * builder was opened on a report that already exists. */
	onIntentSaved?: (savedReportId: string) => void;
	children: React.ReactNode;
}

/** Recursively flips `favorite` on the row matching `rowId` (and leaves every other row, including
 * siblings/children, referentially unchanged) so React only re-renders the affected row/ancestors. */
function toggleFavoriteInRows(rows: ReportRow[], rowId: string): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			changed = true;
			return {...row, favorite: !row.favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = toggleFavoriteInRows(row.children, rowId);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

/** Sets `favorite` on the row matching `rowId` to an explicit value (rather than flipping its
 * current value), so a rollback restores exactly the pre-optimistic-update state even if `rows` was
 * replaced by an intervening refetch in the meantime — flipping the row's CURRENT value would
 * silently write the wrong state in that case, since "current" may no longer be what the optimistic
 * update produced. Leaves every other row, including siblings/children, referentially unchanged. */
function setFavoriteInRows(rows: ReportRow[], rowId: string, favorite: boolean): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			if (row.favorite === favorite) {
				return row;
			}
			changed = true;
			return {...row, favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = setFavoriteInRows(row.children, rowId, favorite);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

/** Finds a row by id anywhere in the tree (including nested children), or null if absent. */
function findRowById(rows: ReportRow[], rowId: string): ReportRow | null {
	for (const row of rows) {
		if (row.id === rowId) {
			return row;
		}
		if (row.children.length > 0) {
			const found = findRowById(row.children, rowId);
			if (found) {
				return found;
			}
		}
	}
	return null;
}

/** A fresh chat-drafted intent states its period via `datePreset`/`startDate`/`endDate` plus
 * `dateScope` alone - the agent that authors it is forbidden from emitting the period's filter
 * conditions itself. The saved-report reload path (`toReportDefinition`) already derives them
 * before this component ever sees the definition; a fresh intent has had no such step, so without
 * this it would fetch completely unfiltered until the first save and reload. Reuses the exact
 * derivation the reload path applies, via `resolveDatePeriod`, so the two paths cannot silently
 * diverge from each other. */
function withDerivedIntentPeriod(definition: ReportDefinitionV1): ReportDefinitionV1 {
	const resolved = resolveDatePeriod(
		definition,
		definition.filter.filterConditions,
		'always',
		new Date(),
		grainOf(definition)
	);
	return {
		...definition,
		datePreset: resolved.datePreset,
		startDate: resolved.startDate,
		endDate: resolved.endDate,
		filter: {...definition.filter, filterConditions: resolved.filterConditions},
	};
}

export function ReportBuilderProvider({
	payload,
	role = 'editor',
	dataSource,
	userId = 'me',
	taxonomy = null,
	savedReportSeed,
	openedFromStorage = false,
	onIntentSaved,
	children,
}: ReportBuilderProviderProps): JSX.Element {
	const resolvedDataSource = dataSource;
	const reportId = payload.draftId ?? 'draft';

	// Only a fresh intent needs the period derived here: a report opened from storage arrives
	// already derived (see `toReportDefinition`), and re-deriving it a second time on top of an
	// already-consistent definition is needless work on every saved-report open.
	const [definition, setDefinition] = useState<ReportDefinitionV1>(() =>
		openedFromStorage ? payload.definition : withDerivedIntentPeriod(payload.definition)
	);
	// The rename field's staged text; null while it is closed. See `commitPendingName` below for how
	// it turns into `definition.name`.
	const [pendingName, setPendingName] = useState<string | null>(null);
	// A reader has no edit mode at all. An editor's landing mode depends on which path opened the
	// builder, and `savedReportSeed` is the signal: it is only supplied when the builder was opened on a
	// report that already exists in the Reports list.
	//
	// A re-opened saved report lands in VIEW mode, so "Edit report" is the only way in. That button is
	// what calls `beginEdit`, and `beginEdit` is what opens a draft — mounting straight into edit mode
	// would let an editor change columns with no draft open, so every save would land on the version
	// readers execute, defeating the isolation the draft exists to provide.
	//
	// A fresh draft has no seed and lands in EDIT mode: it does not exist in the Reports list yet, so
	// there is no published version to isolate readers from and nothing to view — the editor is still
	// building it.
	const [mode, setMode] = useState<BuilderMode>(role === 'reader' || savedReportSeed !== undefined ? 'view' : 'edit');
	const [dirty, setDirty] = useState(false);
	const [grouping, setGroupingState] = useState<GroupingToken[]>((payload.definition.grouping ?? []) as GroupingToken[]);
	const [rows, setRows] = useState<ReportRow[]>([]);
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	const [totalCount, setTotalCount] = useState(0);
	const [capped, setCapped] = useState(false);
	const [cappedTotalEstimate, setCappedTotalEstimate] = useState(0);
	const [totalIsFloor, setTotalIsFloor] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [fetchFailed, setFetchFailed] = useState(false);
	const [fetchAccessDenied, setFetchAccessDenied] = useState(false);
	// Seeded from `savedReportSeed` so a re-opened report updates the entry it already has instead of
	// creating a duplicate on the next save (and so `beginEdit`/`discard` have a report id to act on).
	// Null on the fresh-intent path, where the first save creates the entry.
	const [savedReport, setSavedReport] = useState<SavedReportRef | null>(savedReportSeed ?? null);
	const [saving, setSaving] = useState(false);
	// Mirrors `saving` for the reentrancy check in `save()`, which cannot wait for a render.
	const savingRef = useRef(false);
	const [exporting, setExporting] = useState(false);
	// Mirrors `exporting` for the reentrancy check in `exportCsv()`, which cannot wait for a render.
	const exportingRef = useRef(false);
	// The saved-report id as of the last successful save, readable inside the same call that saved —
	// `savedReport` state is not visible until the next render.
	const savedReportIdRef = useRef<string | null>(savedReportSeed?.id ?? null);
	// Held in a ref so a caller passing an inline arrow does not re-create `save` on every render.
	const onIntentSavedRef = useRef(onIntentSaved);
	onIntentSavedRef.current = onIntentSaved;
	// Bumped by `retryFetch` to re-run the fetch effect without changing the definition.
	const [fetchAttempt, setFetchAttempt] = useState(0);
	const [draftState, setDraftState] = useState<ReportDraftState>(payload.draftState ?? 'UNPUBLISHED');
	const [publishedAt, setPublishedAt] = useState<string | null>(payload.publishedAt ?? null);
	const [draftFilterWillRevert, setDraftFilterWillRevert] = useState<boolean>(payload.draftFilterWillRevert ?? false);
	// Two values, not one, so `discard()` can drop a flag raised inside the discarded draft without
	// lowering one the report already carried in from an earlier session. A single boolean cannot
	// express both: clearing it would lose a real prior edit, keeping it would preserve an edit the
	// user just threw away. The stored half is state rather than a derived constant because a discard
	// re-reads the report and the server's answer supersedes what the session mounted with.
	const [manuallyEditedStored, setManuallyEditedStored] = useState(payload.manuallyEdited === true);
	const [manuallyEditedSession, setManuallyEditedSession] = useState(false);
	const manuallyEdited = manuallyEditedStored || manuallyEditedSession;
	const [publishing, setPublishing] = useState(false);
	const [discarding, setDiscarding] = useState(false);
	const [publishOwed, setPublishOwed] = useState(false);
	const [reloadRequired, setReloadRequired] = useState(false);
	const [definitionMayBeStale, setDefinitionMayBeStale] = useState(false);
	const [storedReportMayHaveChanged, setStoredReportMayHaveChanged] = useState(false);
	// Mirrored for `reconcileStaleDefinition`, which runs from `beginEdit` and has to read this without
	// taking it as a dependency, exactly as it does for `definitionMayBeStale`.
	const storedReportMayHaveChangedRef = useRef(storedReportMayHaveChanged);
	storedReportMayHaveChangedRef.current = storedReportMayHaveChanged;
	// Mirror `definitionMayBeStale`/`dirty` for `reconcileStaleDefinition`, which must read both as they are
	// when it runs rather than as they were when its callback was created — the caller below awaits a lock
	// round trip first, and a callback recreated on every change to either would also churn the identity of
	// `beginEdit`, which effects and memoised consumers key on.
	const definitionMayBeStaleRef = useRef(definitionMayBeStale);
	definitionMayBeStaleRef.current = definitionMayBeStale;
	const dirtyRef = useRef(dirty);
	dirtyRef.current = dirty;
	// The definition this tab last knew report-service had. Seeded from the mount definition — a re-opened
	// report's came from `loadDefinition`, and a fresh draft has no stored report to be out of step with
	// yet — then moved forward by every successful save (that copy is now stored), by the post-discard
	// re-read, and by a stale-view reconcile that proved what is stored.
	//
	// Deliberately NOT the working copy, which may carry unsaved edits: this is the baseline
	// `reconcileStaleDefinition` compares a fresh read against, so it has to be the last version this tab
	// and storage agreed on rather than the one on screen. Moved only when the reconcile is willing to say
	// the view is current again — moving it while the stale statement stands would make the NEXT reconcile
	// see no change and clear a statement that is true.
	const storedDefinitionRef = useRef<ReportDefinitionV1>(payload.definition);
	// Generation of the current lifecycle call, in the spirit of `latestDefinitionRef` below: bumped
	// whenever a call is abandoned because its bounded wait expired. Read by `save()` alone, which is the
	// one call the shared in-flight guard below does not cover (`publish()` awaits it, so folding it in
	// would deadlock): a save whose generation has moved on has been superseded, because the publish that
	// wrapped it gave up waiting, the editor was told the outcome was unknown, and the editor may have
	// edited since. Every other lifecycle call is serialised by the shared guard, so nothing can bump the
	// generation while one of them awaits.
	const lifecycleGenerationRef = useRef(0);
	/** Abandons the current lifecycle call: any result that arrives from now on is superseded. */
	const supersedeLifecycleCall = useCallback(() => {
		lifecycleGenerationRef.current += 1;
	}, []);
	// Shared in-flight guard across ALL THREE lifecycle calls (beginEdit/publish/discard), set
	// synchronously for the same reason as `savingRef` — state is not readable in the same tick.
	// Without a shared guard a discard could run while a publish is still in flight (or vice versa);
	// both write draftState/publishedAt and discard also swaps the definition, so whichever response
	// lands last would win regardless of which action the user meant last. Deliberately separate from
	// `savingRef`: `publish()` calls `save()` internally, and `save()` guards itself independently, so
	// folding `save()` into this guard would make `publish()` deadlock against its own save call.
	const lifecycleInFlightRef = useRef(false);

	/** Bumped every time a lifecycle call finishes, so effects that must not act mid-call have something to
	 * re-run on. `lifecycleInFlightRef` is a ref precisely so that setting it does not render, which leaves
	 * an effect that reads it with no way to re-decide once it clears. */
	const [lifecycleSettled, setLifecycleSettled] = useState(0);

	/** Ends a lifecycle call: clears the in-flight guard and wakes the effects that defer to it. */
	const endLifecycleCall = useCallback(() => {
		lifecycleInFlightRef.current = false;
		setLifecycleSettled(settled => settled + 1);
	}, []);

	// Mirrors the `dataSource` prop for the lock callbacks below, which must reach the CURRENT transport
	// without taking it as a dependency: a consumer that constructs a fresh data source per render (a test
	// or a story that inlines `new MockReportDataSource()`) changes its identity on every render, and a
	// heartbeat callback that changed with it restarted the heartbeat interval every render — so no beat
	// ever survived long enough to fire. Same defence the release path already uses.
	const dataSourceRef = useRef(resolvedDataSource);
	dataSourceRef.current = resolvedDataSource;

	const [lockState, setLockState] = useState<LockState>('unlocked');
	// Mirrors `lockState` for the reconcile path, which has to know whether a loss warning is currently
	// standing without taking `lockState` as a dependency (it is called from effects keyed on the report,
	// not on the lock).
	const lockStateRef = useRef<LockState>(lockState);
	lockStateRef.current = lockState;
	const [lockHolder, setLockHolder] = useState<LockStatus['holder']>(null);
	const [lockedAt, setLockedAt] = useState<string | null>(null);
	// The secret this tab's claim holds, or null while it holds none. A ref, never state: it is never
	// rendered, and a heartbeat tick needs to read it synchronously without waiting on a re-render.
	const lockTokenRef = useRef<string | null>(null);
	// Serialises this tab's own claim attempts. The in-flight flag refuses a second attempt while one is
	// running: a double-pressed Edit Report, or the automatic retry effect firing while a manual claim
	// from `beginEdit` is still in flight, would otherwise race two acquires for the same tab. The
	// generation orders whatever still lands — a claim from a superseded attempt describes a lock a later
	// attempt has already displaced, and storing its token would leave this tab reading 'held' while
	// holding nothing, so it would save as if unlocked and later report a loss that had already happened.
	const lockClaimInFlightRef = useRef(false);
	const lockClaimGenerationRef = useRef(0);
	// Drives the automatic claim's bounded retry. The counter is a ref (it must not itself re-render), the
	// attempt is state because it is what re-runs the claim effect: nothing else about that effect changes
	// when an acquire fails, so without an explicit attempt bump a single transient failure would leave the
	// report unlocked for the whole session.
	const [lockClaimAttempt, setLockClaimAttempt] = useState(0);
	const lockClaimRetriesRef = useRef(0);
	const lockClaimRetryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	// Declared here (ahead of `heartbeat`, which reads it) rather than beside `reconcileStaleDefinition`
	// itself, which is declared much later in this component and would make this a use-before-define.
	// The placeholder is replaced with the real callback on every render, same as `reconcileLockStatusRef`.
	const reconcileStaleDefinitionRef = useRef<(reportId: string) => Promise<void>>(() => Promise.resolve());
	// Mirrors `savedReport.id` for the lock effects below, which must read the CURRENT report id at
	// heartbeat/unmount time without taking `savedReport` as a dependency — `savedReport`'s identity
	// changes on every save, and depending on it would tear down and restart the heartbeat interval
	// (and, for the unmount effect, fire prematurely) on every save while editing.
	const savedReportIdForLockRef = useRef<string | null>(savedReport?.id ?? null);
	savedReportIdForLockRef.current = savedReport?.id ?? null;

	const validColumnIds = useMemo(() => {
		const ids = new Set(flattenFieldPaths(definition.fields));
		FIXED_COLUMN_IDS.forEach(id => ids.add(id));
		return ids;
	}, [definition.fields]);

	const {overlay, write: writeOverlay} = useViewerLayoutOverlay(reportId, userId, validColumnIds);

	// `openedFromStorage` is the provenance signal for the mount definition: it is true only when the
	// builder was opened on a report that already exists in the Reports list, and that definition was read
	// back from storage — so its layout is one this builder wrote, and a leaf field missing from it records
	// a column the user hid. Otherwise the definition arrived in `payload` straight from the generator,
	// whose layout carries no visibility meaning. Deliberately NOT keyed on `savedReport`: saving a fresh
	// report mid-session sets that, and switching to deriving then would start hiding columns the editor is
	// still working with, off a layout this session's own save had just written.
	//
	// Deliberately NOT `savedReportSeed !== undefined`, which asks whether a seed could be BUILT rather
	// than whether the report exists in storage — the distinction the rule near the top of this file
	// draws. `ReportBuilderPage` supplies no seed for a stored report whose `privateAccess` came back
	// null, while still passing `openedFromStorage`; deriving from the seed there reported every hidden
	// column as visible, and the next save then wrote that visibility back and destroyed the hiding.
	const layoutRecordsVisibility = openedFromStorage;
	const canonical = useMemo(
		() => canonicalTableState(definition, layoutRecordsVisibility),
		[definition, layoutRecordsVisibility]
	);

	// In-memory table state for the current session. Initialised from canonical + (for readers)
	// the persisted overlay on top; editors' in-memory edits flow through `save()` instead of the
	// overlay. Once a session has interacted with the table this local state is authoritative —
	// we intentionally do not re-derive it from `canonical`/`overlay` on every render (that would
	// stomp in-flight edits), only on definition identity change (see reset below).
	const initialTableState = useMemo<ReportTableState>(() => {
		if (role !== 'reader' || !overlay) {
			return canonical;
		}
		return {...canonical, ...overlay};
	}, [canonical]);

	const [tableState, setTableState] = useState<ReportTableState>(initialTableState);

	// Fetches on mount and whenever `definition` identity changes (e.g. after `save()`), plus on
	// demand via `retryFetch`. There is no loading spinner, so this stays fire-and-forget; a stale
	// in-flight response from a superseded definition is guarded against via the ref check.
	//
	// Edits are debounced, because a definition edit is not a discrete event: ticking values in a
	// condition's value picker produces a new definition per click, and running the whole report on each
	// one put dozens of fetches on the wire in a couple of seconds. The mount fetch is NOT debounced —
	// there is nothing to collapse it with, and delaying it would leave the surface blank for no reason.
	// `hasFetched` flips to true once the first response lands, so the empty state can wait for a
	// real (possibly zero-row) result rather than rendering during the initial in-flight window.
	// A rejection (non-2xx or network failure) is caught and surfaced as `fetchFailed` — leaving it
	// unhandled would both render a bare empty grid and raise an unhandled promise rejection.
	const latestDefinitionRef = useRef(definition);
	latestDefinitionRef.current = definition;
	// Read inside the effect instead of depending on `grouping` directly - see `fetchFieldsKey` below for
	// why the effect must not re-run on every grouping change, and this ref for how it still sends the
	// grouping current as of whenever it does run.
	const latestGroupingRef = useRef(grouping);
	latestGroupingRef.current = grouping;
	// False until the mount fetch has been issued, which is one of the runs that must not be delayed.
	const hasScheduledFetchRef = useRef(false);
	// The `fetchAttempt` this effect last ran for, so an explicit retry can be told apart from an edit.
	const lastFetchAttemptRef = useRef(fetchAttempt);
	// The filter set the last audit-log `Searched` event reported. The fetch effect re-runs for
	// far more than a search — a rename mutates `definition`, a column or grouping change moves
	// `fetchFieldsKey`, "try again" bumps `fetchAttempt` — and every one of those would otherwise
	// emit another Searched carrying the SAME filter names, inflating the count the event exists
	// to measure. Null until the first one fires.
	const lastTrackedFilterSignatureRef = useRef<string | null>(null);

	// The taxonomy is what says which leaves are durations, dates or percentages - a distinction no
	// value carries on the wire, where all three arrive as a bare number or string. It resolves
	// asynchronously, so it can land after the rows did.
	//
	// For the task grain that costs nothing to ignore: every task leaf the mapper types is already
	// typed by its shape or collapsed by the compound handling, so the taxonomy changes no cell. For
	// any other grain it changes several, so the rows are fetched again once the taxonomy arrives -
	// once, because it resolves once. Deliberately not the other way round (holding the first fetch
	// until the taxonomy resolves): a taxonomy that never resolves is indistinguishable from one
	// still loading, and that would leave the table permanently empty rather than briefly untyped.
	//
	// Read from the field-tree root, the same signal the mapper keys cells by, and NOT from the
	// stored report type. The two disagree on a report saved before the type was recorded: it
	// carries the task type alongside another grain's fields, so a type-based test withholds the
	// taxonomy from rows the mapper is keying as project-status. Those rows come back correctly
	// keyed but untyped - minutes shown as a bare count under an hours heading, a percentage
	// without its unit, and no grouped subtotals at all, since a total is only summed over
	// duration cells.
	const rowsNeedTaxonomy = grainRootOf(definition) !== TASK_GRAIN_ROOT;
	const taxonomyForRows = rowsNeedTaxonomy ? taxonomy : null;

	// Grouping is applied client-side over rows already fetched (`useClientGrouping`), but report-service
	// only returns a field the fetch body names, so grouping by a field the definition does not show
	// needs that field fetched (see `fetchFieldsFor`). A grouping change therefore needs a
	// refetch exactly when it changes the outgoing field tree, and `fetchFieldsFor` is the function
	// that decides that, so this serialises its result and uses that (not `grouping` itself) as the
	// dependency below: two groupings that resolve to the same tree - any tokens whose fields the
	// definition already selects - serialise identically and trigger no re-run, while a token whose
	// field is genuinely missing changes the string and triggers exactly one.
	//
	// `taxonomyForRows`, the same catalog the fetch itself is handed, so the key and the body cannot
	// disagree. It is null on the task grain, whose token paths are fixed and need no catalog.
	const fetchFieldsKey = useMemo(
		() => JSON.stringify(fetchFieldsFor(definition, grouping, taxonomyForRows)),
		[definition, grouping, taxonomyForRows]
	);
	// A few cells carry a display label the mapper resolves rather than passing through (a project's
	// status and stage), so the fetch needs the reader's language. Held in a ref and deliberately
	// kept OUT of the effect's dependencies: `formatMessage`'s identity changes when the active
	// locale's messages do, and listing it would turn a locale load into another full report fetch.
	// The consequence is that a locale switch relabels on the next fetch rather than instantly,
	// which is what changing language in this app does anyway.
	const {formatMessage} = useIntl();
	const formatLabelRef = useRef(formatMessage);
	// Written in an effect rather than in the render body: a render React later discards must not
	// leave its formatter behind in the ref. The `useRef` initialiser already seeds it for the first
	// fetch, and effects run in declaration order, so this one has committed before the fetch effect
	// below reads it.
	useEffect(() => {
		formatLabelRef.current = formatMessage;
	});

	useEffect(() => {
		let cancelled = false;
		const isStale = (): boolean => cancelled || latestDefinitionRef.current !== definition;
		setFetchFailed(false);
		setFetchAccessDenied(false);
		const runFetch = (): void => {
			if (cancelled) {
				return;
			}
			void resolvedDataSource
				.fetchRows(
					definition,
					{offset: 0, limit: 1000},
					taxonomyForRows,
					formatLabelRef.current,
					latestGroupingRef.current
				)
				.then(
					result => {
						if (isStale()) {
							return;
						}
						setRows(result.rows);
						setTotalCount(result.totalCount);
						setCapped(result.capped);
						setCappedTotalEstimate(result.cappedTotalEstimate);
						setTotalIsFloor(result.totalIsFloor);
						setHasFetched(true);

						// Audit-log search usage. Filter FIELD NAMES and a count only — never the
						// values typed into them, which are audit content.
						//
						// Once per distinct filter set, not once per fetch. The effect also re-runs
						// on a rename, a column/grouping change and a "try again" click, none of
						// which is a new search, and all of which would report the same filter
						// names again.
						if (grainOf(definition) === AUDIT_LOG_TAXONOMY_TYPE) {
							// Returns null when this fetch is not a new search. The signature behind that
							// decision covers operator and values as well as field — switching Actor A to
							// Actor B is a different search, and keying on field names alone reported
							// nothing for it (PIKA-772 QA, D2). See auditLogSearchSignature.
							const report = auditLogSearchReport(
								definition.filter?.filterConditions,
								lastTrackedFilterSignatureRef.current
							);
							if (report) {
								lastTrackedFilterSignatureRef.current = report.signature;
								trackAuditLogReportSearched(report.filterTypes, result.totalCount);
							}
						}
					},
					error => {
						if (isStale()) {
							return;
						}
						// Clear any previous result so a stale table can't linger under the error.
						setRows([]);
						setTotalCount(0);
						setCapped(false);
						setCappedTotalEstimate(0);
						setTotalIsFloor(false);
						setHasFetched(false);
						// Set before `setFetchFailed`, not after: this runs in a promise callback, which React
						// does not batch in legacy mode, so each setState commits its own render. Flipping
						// `fetchFailed` first would paint one frame of the generic error — retry button and all
						// — before the refusal was known, which is the exact affordance the refusal state exists
						// to withhold.
						setFetchAccessDenied(isReportDataAccessDenied(error));
						setFetchFailed(true);
					}
				);
		};

		// Only an edit-driven rerun is debounced. The mount fetch has nothing to collapse with, and a
		// retry is a deliberate click on "try again" — delaying either just makes the surface look stuck.
		const isRetry = fetchAttempt !== lastFetchAttemptRef.current;
		lastFetchAttemptRef.current = fetchAttempt;
		if (!hasScheduledFetchRef.current || isRetry) {
			hasScheduledFetchRef.current = true;
			runFetch();
			return () => {
				cancelled = true;
			};
		}

		const timer = setTimeout(runFetch, FETCH_DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
		// `fetchFieldsKey` is a dependency, not `grouping` itself - see where it is computed above. Reading
		// `grouping` inside the effect goes through `latestGroupingRef` instead, so the request sent still
		// carries whatever grouping is current when the effect actually runs, without that value's own
		// (mostly irrelevant) changes deciding when it runs.
	}, [definition, resolvedDataSource, fetchAttempt, taxonomyForRows, fetchFieldsKey]);

	const retryFetch = useCallback(() => {
		setFetchAttempt(n => n + 1);
	}, []);

	const updateSavedReportPrivateAccess = useCallback((privateAccess: boolean) => {
		setSavedReport(prev => (prev ? {...prev, privateAccess} : prev));
	}, []);

	const setName = useCallback((name: string) => {
		setDefinition(prev => ({...prev, name}));
		setDirty(true);
	}, []);

	/** See the interface doc above. This is the one place the staged-name rule lives — trim, compare,
	 * apply-or-fall-back, always close — so `ReportPageHeader`'s submit/Enter path and `save`/`exportCsv`
	 * (which call this themselves) can never disagree about what a commit does. */
	const commitPendingName = useCallback((): string => {
		if (pendingName === null) {
			return definition.name;
		}
		const trimmed = pendingName.trim();
		setPendingName(null);
		if (trimmed === '' || trimmed === definition.name) {
			return definition.name;
		}
		setName(trimmed);
		return trimmed;
	}, [pendingName, definition.name, setName]);

	/** Tasks are matched by OVERLAP with the period, not containment: a task counts when it starts on or
	 * before the period ends and finishes on or after the period begins. Filtering for tasks fully inside
	 * the window would hide exactly the long-running work a period report is usually about. Each side is
	 * independent, so an open start or end simply drops its condition. */
	const setPeriod = useCallback((startDate: string | null, endDate: string | null) => {
		setDefinition(prev => ({
			...prev,
			startDate: startDate ?? undefined,
			endDate: endDate ?? undefined,
			datePreset: undefined,
			filter: {
				...prev.filter,
				filterConditions: withPeriodFilterConditions(
					prev.filter.filterConditions,
					startDate,
					endDate,
					prev.dateScope,
					grainOf(prev)
				),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const setPeriodPreset = useCallback((token: DatePresetToken) => {
		const preset = findRelativeDatePreset(token);
		if (!preset) {
			return;
		}
		const {startDate, endDate} = preset.resolve(new Date());
		setDefinition(prev => ({
			...prev,
			startDate,
			endDate,
			datePreset: token,
			filter: {
				...prev.filter,
				filterConditions: withPeriodFilterConditions(
					prev.filter.filterConditions,
					startDate,
					endDate,
					prev.dateScope,
					grainOf(prev)
				),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	/** Changes which date field(s) the period filters on, keeping the range itself. Re-derives the
	 * conditions rather than editing them: withPeriodFilterConditions clears the outgoing scope's
	 * fields, so switching scope cannot leave two incompatible date windows ANDed together. */
	const setPeriodScope = useCallback((scope: DateScope) => {
		setDefinition(prev => {
			// The EFFECTIVE period, via the same derivation the date control displays - not the raw
			// `startDate`/`endDate` fields. A definition whose window lives only in its filter
			// conditions carries no top-level dates, so reading those directly rebuilds the conditions
			// from an empty range: every date condition is dropped and none replaces it, leaving the
			// report unfiltered while the control still shows the window it was filtering on.
			// Resolving through the fallback also promotes that window onto the definition, so nothing
			// downstream has to re-derive it.
			const {startDate, endDate} = periodOf(prev);
			return {
				...prev,
				startDate: startDate ?? undefined,
				endDate: endDate ?? undefined,
				dateScope: scope,
				filter: {
					...prev.filter,
					filterConditions: withPeriodFilterConditions(
						prev.filter.filterConditions,
						startDate,
						endDate,
						scope,
						grainOf(prev)
					),
				},
			};
		});
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const setGrouping = useCallback((g: GroupingToken[]) => {
		setGroupingState(g);
		setDirty(true);
	}, []);

	/** Sorting, resizing and reordering are the only table mutations reachable in view mode — the
	 * toolbar that carries every other control is not rendered there. For an editor they are a
	 * viewing gesture, not an edit to the report, so they must not set `dirty`: that would arm the
	 * unsaved-changes pill and the leave-without-saving overlay against someone who only looked.
	 *
	 * An editor's view-mode rearrangement is therefore in-memory for the session only. It is
	 * deliberately NOT written to the viewer overlay: that overlay is read back on mount for readers
	 * alone, so writing it here would persist a layout nothing ever restores. */
	const updateTableState = useCallback(
		(patch: Partial<ReportTableState>) => {
			setTableState(prev => ({...prev, ...patch}));
			if (role === 'reader') {
				writeOverlay(patch);
			} else if (mode === 'edit') {
				setDirty(true);
			}
		},
		[role, mode, writeOverlay]
	);

	const save = useCallback(async () => {
		// Reentrancy guard, and deliberately a ref rather than the `saving` state: the state only gates
		// the Save button after a render has committed, so two calls raised before that commit would both
		// see `savedReport` as null, both take the create branch, and leave two Reports-list entries for
		// one draft with only the later one tracked. A ref is set synchronously, so the second call is
		// refused whatever the render timing.
		if (savingRef.current) {
			return false;
		}
		if (lockState === 'lost' || lockState === 'blocked') {
			// This tab does not hold the edit lock — saving would write over whoever does. 'blocked' is
			// reachable in edit mode (a re-entry into edit against a lock someone else has since taken), and the
			// server refuses a non-holder's save anyway, so refusing here is what turns an unexplained failure
			// into the state the banner already describes. Refused WITHOUT clearing `dirty`: the unsaved edits
			// are exactly what a reload-and-reopen would lose, so they stay on screen (and Save stays looking
			// enabled/retriable) until the editor acts.
			return false;
		}
		savingRef.current = true;
		const generation = lifecycleGenerationRef.current;
		// Commits an open, uncommitted rename before anything is serialised: without this an editor who
		// typed a new name and hit Publish/Save without pressing the tick would have it silently
		// discarded — the field was staged local state nobody read here. `commitPendingName` resolves
		// synchronously, so the override below (rather than reading `definition.name`) is what makes the
		// just-committed name available immediately, before this render's `definition` closure catches up.
		const resolvedName = commitPendingName();
		const nextDefinition = serializeDefinition({...definition, name: resolvedName}, tableState, grouping);
		setSaving(true);
		try {
			const ref = await resolvedDataSource.saveDefinition(
				nextDefinition,
				savedReport,
				payload.prompt ?? null,
				manuallyEdited,
				lockTokenRef.current
			);
			// Recorded even when this save has been superseded: the id is the report's identity, never
			// something the user can have changed in the meantime, and dropping it would let a later save
			// take the create branch and leave two Reports-list entries for one draft.
			setSavedReport(ref);
			// Read before the assignment below overwrites it: this is the only save that turns the
			// payload into a report with an id, and the only one anybody upstream needs to hear about.
			const wasUnsaved = savedReportIdRef.current === null;
			savedReportIdRef.current = ref.id;
			// The save landed, so this definition is what report-service holds now — recorded before the
			// supersede check below, which decides what goes on SCREEN and not what is stored. Without this a
			// later stale-view reconcile would compare a fresh read against the pre-save baseline and report
			// this tab's own saved edits as somebody else's change.
			storedDefinitionRef.current = nextDefinition;
			if (wasUnsaved) {
				onIntentSavedRef.current?.(ref.id);
			}
			if (generation !== lifecycleGenerationRef.current) {
				// The one generation check that can fire, and the reason the counter exists: `save()` is the
				// only call the shared lifecycle guard does not cover, so a `publish()` that gave up waiting on
				// this very save has already told the editor the outcome could not be confirmed, and the editor
				// may have edited since. The save itself did land, so say so, but do not adopt the definition it
				// saved over whatever is on screen now. Every other lifecycle call holds that guard for its whole
				// run, so no generation can move while one of them awaits.
				return true;
			}
			// Adopting the serialised definition also re-runs the fetch (the effect keys on definition
			// identity), so what is on screen after a save is the saved definition run against live
			// data — the same thing re-opening the report from the Reports list will show.
			setDefinition(nextDefinition);
			setDirty(false);
			// This tab's copy is now what is stored, so the view and storage agree again for it — whatever
			// another holder had stored in the meantime has just been superseded by this save.
			setDefinitionMayBeStale(false);
			setStoredReportMayHaveChanged(false);
			return true;
		} catch {
			// Deliberately swallowed. The draft is left dirty and editable so Save stays enabled and the
			// user can retry; re-throwing would surface as an unhandled rejection in any caller that
			// does not chain a catch, which is what tore the surface down before.
			return false;
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	}, [
		definition,
		tableState,
		grouping,
		resolvedDataSource,
		savedReport,
		payload.prompt,
		manuallyEdited,
		lockState,
		commitPendingName,
	]);

	/** Opens a claim attempt and returns its generation, which supersedes every attempt before it. Every
	 * call that ends in `applyLockClaim` must take its generation from here FIRST. */
	const beginLockClaim = useCallback((): number => {
		lockClaimGenerationRef.current += 1;
		return lockClaimGenerationRef.current;
	}, []);

	/** Applies a lock claim (from `acquireLock`) to local state:
	 * - a token means this tab now holds it;
	 * - a null token with a live lock means someone else does;
	 * - a null token with NO live lock is report-service's stale-token conflict, which names no holder.
	 *   Nothing is held, so there is nothing to take over and the lock reads as free — the claim is simply
	 *   retriable, rather than showing a blocked banner with an unnamed avatar.
	 *
	 * `generation` comes from `beginLockClaim`. A claim whose generation has been superseded is dropped:
	 * a later attempt has already displaced whatever lock it describes, so its token is dead and storing
	 * it would leave this tab reading 'held' while holding nothing. Dropped rather than released for the
	 * same reason — there is nothing left to release. */
	const applyLockClaim = useCallback((claim: LockClaim, generation: number) => {
		if (generation !== lockClaimGenerationRef.current) {
			return;
		}
		if (claim.token) {
			// Reset only on a claim that actually GRANTED the lock. Reset for any settled claim — which is
			// what "the transport works" argued for — it sat above the bound the no-lock branch below then
			// tests, so that branch always read 0 < LIMIT, scheduled a retry, re-ran this same path and
			// zeroed the counter again: an unbounded 5s mutation loop for the lifetime of the page. The
			// counter is shared with `retryAfterFailedClaim`, which is bounded precisely because it never
			// resets.
			lockClaimRetriesRef.current = 0;
			lockTokenRef.current = claim.token;
			setLockState('held');
			setLockHolder(claim.status.holder);
			setLockedAt(claim.status.lockedAt);
			return;
		}
		lockTokenRef.current = null;
		if (!claim.status.locked) {
			setLockState('unlocked');
			setLockHolder(null);
			setLockedAt(null);
			// Nothing was claimed and nothing is held — exactly the failure the auto-claim effect exists to
			// prevent (no lock, no heartbeat, and a second editor's `beginEdit` then succeeds). But that
			// effect's own guard is `lockState !== 'unlocked'`, and this claim started from 'unlocked' too
			// (its own guard requires it), so a value-identical `setLockState('unlocked')` triggers no
			// re-render and the effect never re-runs on its own. Retried on the same bounded schedule as an
			// outright acquire failure, via the attempt counter that IS a dependency of that effect.
			if (lockClaimRetriesRef.current < LOCK_CLAIM_RETRY_LIMIT) {
				lockClaimRetriesRef.current += 1;
				lockClaimRetryTimerRef.current = setTimeout(
					() => setLockClaimAttempt(attempt => attempt + 1),
					LOCK_CLAIM_RETRY_DELAY_MS
				);
			}
			return;
		}
		setLockState('blocked');
		setLockHolder(claim.status.holder);
		setLockedAt(claim.status.lockedAt);
	}, []);

	/** Reconciles a lock READ (the initial load, and a pushed socket event's refetch — never a claim
	 * attempt of this tab's own) against local state, per whether this tab currently holds a token:
	 * - locked by someone else, and this tab holds no token: 'blocked'.
	 * - locked by someone else, and this tab thought it held one: the session was broken elsewhere
	 *   (it lapsed and someone else has since claimed it) — 'lost'. The token is dropped,
	 *   never replaced with anything from `status`: adopting a new token here would silently steal the
	 *   lock back from whoever holds it now.
	 * - free, and this tab holds a token: left alone ('held') — the token is still valid.
	 * - free, and this tab holds no token: 'unlocked'.
	 * - locked and `heldByViewer`, but this tab holds no token: `heldByViewer` is resolved server-side by
	 *   user identity, not by this tab's token, so this is a DIFFERENT tab (or an earlier session) of the
	 *   same signed-in user. Per the accepted "two tabs of one user, last write wins" design this tab is
	 *   not blocked by it — cleared to 'unlocked' so a stale holder name from a PRIOR block does not
	 *   survive the same user re-claiming from elsewhere. */
	const reconcileLockStatus = useCallback((status: LockStatus) => {
		const thisTabHolds = lockTokenRef.current !== null;
		if (status.locked && status.heldByViewer === null && thisTabHolds) {
			// The server could not resolve whether this viewer is the holder (a user-service lookup it needs,
			// and a transient failure there is routine). Every branch below would read that as someone else
			// holding the lock and drop a token this tab may still legitimately hold, which blocks every other
			// editor for the rest of the TTL and tells the genuine holder their session expired. Nothing is
			// recorded from a read that answers nothing; the next one decides.
			return;
		}
		if (status.locked && status.heldByViewer && !thisTabHolds) {
			// Same user, a different tab (or an earlier session) holds it now — not a takeover, and per the
			// accepted "two tabs of one user, last write wins" design this tab is not blocked by it either.
			// Without this branch, a tab that was 'blocked' by someone else who has since released, then
			// re-claimed by this SAME user from another tab, showed the ORIGINAL holder's name forever:
			// the branch below does not fire because `heldByViewer` is now true, and the freed branch below
			// does not fire because the report is still locked. Cleared to what this tab can actually prove:
			// it holds no token of its own, and nothing is blocking it.
			//
			// Not from 'lost', for the same reason the two branches below early-return on it: the same user
			// re-claiming from a second tab is reachable while this tab sits in 'lost' with unsaved edits, and
			// clearing to 'unlocked' there takes down the only banner telling them those edits are stranded —
			// after which `save()`, which gates on lock state alone, writes the pre-loss copy over storage.
			if (lockStateRef.current === 'lost') {
				return;
			}
			setLockState('unlocked');
			setLockHolder(null);
			setLockedAt(null);
			return;
		}
		if (status.locked && !status.heldByViewer) {
			// A read that lands after this tab already recorded losing the lock must not downgrade 'lost'
			// to 'blocked': by then `thisTabHolds` is false (the token was already cleared below), so
			// without this guard a socket-driven refetch racing the loss silently swapped the banner that
			// tells the editor their unsaved edits are stranded for one that does not — and let the
			// auto-claim effect, gated only on `lockState !== 'unlocked'`, treat 'blocked' → free later as
			// license to silently re-acquire.
			if (lockStateRef.current === 'lost') {
				return;
			}
			if (thisTabHolds) {
				lockTokenRef.current = null;
				setLockState('lost');
				// Another user holds the lock now, so they may have saved and published in the meantime: what is
				// on screen can no longer be spoken for. Stated, not gated — see `definitionMayBeStale`.
				setDefinitionMayBeStale(true);
			} else {
				setLockState('blocked');
			}
			setLockHolder(status.holder);
			setLockedAt(status.lockedAt);
			return;
		}
		if (!status.locked && !thisTabHolds) {
			// 'lost' is deliberately NOT collapsed into 'unlocked', for the same reason leaving edit mode does
			// not collapse it: the warning is the only thing telling the editor that the edits still on screen
			// were never saved, and the lock going free does not answer that. It stands, along with the holder
			// that broke the session, until the user acts on it.
			if (lockStateRef.current === 'lost') {
				return;
			}
			// A lock this tab was BLOCKED on has gone free, so the editor who held it ended their session —
			// by publishing, which changes what is stored, or by discarding, which does not. report-service
			// announces the lock change but nothing announces a publish, so which of the two happened is not
			// knowable from here. The banner is worded for exactly that uncertainty: the stored report MAY
			// have changed, so reload to see it.
			//
			// Without this, a viewer watched the holder's banner disappear and was left reading a report that
			// silently no longer matched what was stored, with nothing on screen suggesting a reload.
			// Deliberately keyed on the PREVIOUS state being 'blocked': a tab that was already 'unlocked' is
			// reconciling a lock nobody took, and has nothing to be stale about.
			if (lockStateRef.current === 'blocked') {
				setStoredReportMayHaveChanged(true);
			}
			setLockState('unlocked');
			setLockHolder(null);
			setLockedAt(null);
		}
	}, []);

	// Lets the blocked-lock poll call the reconciler without taking it as a dependency; see
	// `pollBlockedLock` for why that callback's identity has to stay stable.
	const reconcileLockStatusRef = useRef(reconcileLockStatus);
	reconcileLockStatusRef.current = reconcileLockStatus;

	/** Reads the lock and reconciles it, dropping the result if this tab's lock moved on while the read was
	 * in flight. Every status read a tab issues while it may HOLD the lock goes through here: the mount
	 * read, the socket-driven refetch, and the re-read after a refused lifecycle call.
	 *
	 * `pollBlockedLock` deliberately does not, and is the only exception. It runs solely while this tab is
	 * blocked, and it re-checks `lockStateRef.current === 'blocked'` before applying anything — a tab that
	 * has since acquired the lock is no longer blocked, so its read is dropped by that check instead. The
	 * token-and-generation guard here would be inert for it anyway: reaching the branch that clears a
	 * token requires holding one, which is 'held', not 'blocked'.
	 *
	 * The guard is the whole point: nothing about a status read is atomic with the claim path, so a read
	 * issued while another user held the lock can land after this tab has legitimately acquired it — the
	 * mount read is issued before first paint and Edit is live from first paint, so the window is a normal
	 * one, not a pathological one. Applied unguarded, that stale payload takes the "someone else holds it
	 * and this tab thought it held one" branch and puts this tab in 'lost': saves and publishes refused,
	 * the heartbeat stopped, the release a no-op, and the state sticky until a reload — while the lock this
	 * tab genuinely owns blocks everyone else for the full TTL.
	 *
	 * The token and the claim generation together identify the state being reconciled. If either has moved,
	 * the read describes a lock that is no longer the one in question. An unmount cannot help here: the
	 * read has already been issued, and `relay-runtime` dedupes identical in-flight requests, so a
	 * corrective refetch collapses onto the same stale request and returns the same stale payload. */
	const readAndReconcileLock = useCallback(
		(reportId: string) => {
			const token = lockTokenRef.current;
			const generation = lockClaimGenerationRef.current;
			void resolvedDataSource.lockStatus(reportId).then(
				status => {
					if (lockTokenRef.current !== token || lockClaimGenerationRef.current !== generation) {
						return;
					}
					reconcileLockStatus(status);
				},
				() => undefined
			);
		},
		[resolvedDataSource, reconcileLockStatus]
	);

	// Reads the lock once on mount for a report that already exists, so an editor sees the holder (and
	// Edit disabled) on load — before pressing Edit, and independent of whether a socket event ever
	// arrives. Keyed on the SEED, not the evolving `savedReport` state: the seed is stable for the life
	// of this provider, so this runs once, rather than re-querying (and potentially clobbering a 'held'
	// state this tab has since earned) after every save.
	//
	// Editors only, like the auto-claim effect below. Nothing a reader can see renders from the lock —
	// the holder line and the Edit button are both behind `role === 'editor'` — so for a reader this read
	// answers a question nobody asks, and worse, it is what put a reader into 'blocked' and started the
	// poll below: a report with one editor and thirty readers open cost thousands of requests an hour,
	// each fanning out to report-service, user-service and a person load, none of which can change a pixel.
	useEffect(() => {
		const reportId = savedReportSeed?.id;
		if (!reportId || role === 'reader') {
			return;
		}
		// Guarded like every other status read: this one is issued before first paint while Edit is already
		// live, so it is the likeliest of them all to land after this tab has claimed the lock itself. An
		// unmount flag cannot cover that — the tab is still mounted, it just holds a newer truth.
		readAndReconcileLock(reportId);
	}, [role, savedReportSeed, readAndReconcileLock]);

	// Refreshes the lock status whenever a pushed socket event says THIS report's lock changed hands, so
	// a viewer's banner (and a held editor's loss detection) does not wait on the next 40s heartbeat or a
	// page reload. The socket is a trigger only — `lockStatus` stays the source of truth that is actually
	// applied — so nothing here breaks when sockets are off; it just stops updating on its own.
	//
	// Deliberately not the `withSimpleSocketHandling` HOC: that HOC wraps its component in `withRouter`,
	// which needs a Router in the tree, and this context has no business depending on one. Subscribing
	// directly to the same `EVENT_ID.SOCKET_NOTIFY` pub-sub the HOC is built on, and doing the same
	// entityIds-aware filtering it does (never the type-only filtering `withSocketHandling` does), gets
	// the same triggering without that dependency.
	//
	// It is NOT identical, in one deliberate way: the HOC defers its refetch while `document.hidden` and
	// replays it on the next `visibilitychange`, whereas this refetches immediately. A lock's holder is
	// exactly what a backgrounded tab must not be stale about — it decides whether this tab may still
	// save — and the refetch is one small read, so it is not worth deferring.
	useEffect(() => {
		const reportId = savedReportSeed?.id;
		// Editors only, for the same reason as the mount read: a reader renders nothing from the lock, so
		// there is nothing for a refetch to update.
		if (!reportId || role === 'reader') {
			return;
		}
		// report-service's socket payload carries the raw report-service UUID, not the Relay global id
		// every lock call on this client uses — the same decode `ProgramContextProvider` uses for its own
		// entityIds. Guarded: an id that is not valid base64 (a hand-rolled id in a test/story, or a
		// future non-Relay id) cannot be decoded, and this subscription is simply not possible for it —
		// that must not crash the provider's mount.
		let reportUuid: string;
		try {
			reportUuid = Util.getUUIdFromBase64String(reportId);
		} catch {
			return;
		}
		const handleSocketNotify = (events: Array<{eventType?: string; eventAction?: string; entityIds?: string[]}>) => {
			const namesThisReport = events.some(
				event =>
					event.eventType === SOCKET_EVENT_TYPE.REPORT_LOCK &&
					event.eventAction === SOCKET_ACTION.UPDATE &&
					Array.isArray(event.entityIds) &&
					event.entityIds.includes(reportUuid)
			);
			if (namesThisReport) {
				// Guarded, like every other status read: unsubscribing on cleanup cannot stop a read already
				// issued from landing on a lock this tab has since claimed.
				readAndReconcileLock(reportId);
			}
		};
		subscribe(EVENT_ID.SOCKET_NOTIFY, handleSocketNotify);
		return () => unsubscribe(EVENT_ID.SOCKET_NOTIFY, handleSocketNotify);
	}, [role, savedReportSeed, readAndReconcileLock]);

	/** Releases the lock this tab holds, if any. Reads the report id and token via refs rather than
	 * closed-over state, so callers can use it as a stable dependency that never itself triggers the
	 * effects below on every save or claim. Best-effort: a failed release is not retried — the lock
	 * still expires on the server's TTL, so nothing is left permanently stuck. */
	const releaseHeldLock = useCallback(() => {
		const reportId = savedReportIdForLockRef.current;
		const token = lockTokenRef.current;
		if (!reportId || !token) {
			return;
		}
		lockTokenRef.current = null;
		void resolvedDataSource.releaseLock(reportId, token).catch(() => undefined);
	}, [resolvedDataSource]);

	// Held in a ref so the effect below can call the current release without depending on its identity.
	// `releaseHeldLock` closes over the `dataSource` prop, and a consumer that constructs a fresh data
	// source per render (a test or a story that inlines `new MockReportDataSource()`, and any future
	// caller that does the same) would otherwise re-run that effect's cleanup on every render — releasing
	// the live lock mid-edit and leaving `lockState` 'held' with a null token, so the heartbeat silently
	// no-ops and saves proceed unlocked. Production escapes it only because the real data source happens
	// to be a module singleton, which is not a property the effect should depend on.
	const releaseHeldLockRef = useRef(releaseHeldLock);
	releaseHeldLockRef.current = releaseHeldLock;

	// Releases the lock once this tab is out of edit mode and holding it. Deliberately NOT a
	// `beforeunload` handler: tab close is handled by the server's lock TTL by design, not by a
	// best-effort unload beacon.
	//
	// Keyed on HOLDING the lock as well as on the mode, symmetrically with the heartbeat below, because
	// the two have to agree about which tokens they are responsible for. Keyed on `mode` alone this could
	// only fire on a mode transition, and an acquire can settle after one: a create-then-publish claims
	// while `mode` is still 'edit', publish flips to 'view' before the claim lands, and the release then
	// ran against a null token and no-opped. `mode` never changed again, so nothing released, while the
	// heartbeat — which IS keyed on holding — renewed the lock every 40s for as long as the page stayed
	// open, blocking every other editor indefinitely. With no takeover flow that is strictly worse than
	// the bounded lapse it was avoiding.
	//
	// Unmount is covered by the effect below rather than by a cleanup here: `lockState` is a dependency
	// now, so a cleanup would fire on every lock-state change and release a token `beginEdit` had just
	// been granted.
	//
	// Skipped entirely while a lifecycle call is in flight, because `beginEdit` reaches 'held' with `mode`
	// still 'view': it records the granted claim, then awaits `enterDraft` before switching mode. Firing
	// in that window released the token `beginEdit` had been granted one line earlier, left the tab in
	// edit mode holding nothing, and let the auto-claim effect immediately acquire a THIRD time. The mode
	// switch that ends the window is itself a dependency, so the effect re-runs and re-decides once
	// `beginEdit` has finished — by which point `mode === 'edit'` and there is nothing to release.
	useEffect(() => {
		if (mode !== 'edit' && lockState === 'held' && !lifecycleInFlightRef.current) {
			releaseHeldLockRef.current();
			// Deliberately NOT clearing `definitionMayBeStale` here. "View mode re-reads the report" is not
			// true — nothing in this codebase re-reads on a bare mode transition; only `reconcileStaleDefinition`
			// (from `beginEdit`), `discard()` and a successful `save()` do, and each of those already clears
			// the flag itself once it has actually verified or replaced what is on screen. Clearing it here
			// too would let a path that reaches view mode some other way silently drop a statement that is
			// still true — `beginEdit` would then re-enter edit without comparing against storage, and
			// `publish()` would stop refusing to publish over it.
			// 'lost' is deliberately NOT collapsed into 'unlocked'. The warning is the only thing telling the
			// editor that the edits still on screen were never saved, and leaving edit mode does not answer
			// that — so it stands until the user acts on it (a retry, which re-claims the lock).
			setLockState(prev => (prev === 'held' ? 'unlocked' : prev));
		}
		// `lifecycleSettled` is what re-runs this once the in-flight guard above clears. The guard makes the
		// effect decline to act, and `lifecycleInFlightRef` is a ref precisely so that clearing it does not
		// render — so without a bump there is nothing to reconsider a token that was granted during the
		// window, and the heartbeat would renew it for as long as the page stayed open.
		//
		// It is a backstop, not a path anyone has reproduced. Every `beginEdit` route that ends up holding a
		// token does reach `setMode('edit')`, whose own render re-runs this — including the `enterDraft`
		// timeout, whose `return` sits inside a `try` with an unconditional `setMode('edit')` in its
		// `finally`. Do not delete this on the strength of that one path failing to reproduce: the
		// stand-down above is unconditional, so anything that reaches 'held' while a lifecycle call is in
		// flight and does not later change `mode` or `lockState` depends on this to be reconsidered at all.
	}, [mode, lockState, lifecycleSettled]);

	// THE INVARIANT: if this tab holds a token, it heartbeats that token and it eventually releases that
	// token — whatever `mode` says. This effect is the "eventually releases" half for a teardown, which the
	// effect above cannot cover: it has `lockState` as a dependency, so a cleanup there would fire on every
	// lock-state change rather than only on unmount. Mounted once (no dependencies), so it fires on unmount
	// and never on a mode or lock-state change — releasing on either would drop the token `beginEdit` had
	// just been granted.
	useEffect(() => {
		return () => {
			releaseHeldLockRef.current();
		};
	}, []);

	// Clears a pending claim retry on unmount, so a timer cannot fire into an unmounted provider.
	useEffect(() => {
		return () => {
			if (lockClaimRetryTimerRef.current !== undefined) {
				clearTimeout(lockClaimRetryTimerRef.current);
			}
		};
	}, []);

	// Claims the lock for any report being edited, whichever path got the builder into edit mode.
	// `beginEdit` covers re-opening a saved report, but a report created inside the builder is ALREADY in
	// edit mode when its first save gives it an id, so `beginEdit` never runs for it: without this, no
	// lock is held, no heartbeat runs, and a second editor's `beginEdit` succeeds — both then overwrite
	// each other, which is the failure the lock exists to prevent. An unsaved draft has no report row to
	// lock, so nothing is claimed until an id exists.
	const savedReportIdForLockEffect = savedReport?.id ?? null;
	useEffect(() => {
		if (role === 'reader' || mode !== 'edit' || savedReportIdForLockEffect === null) {
			return;
		}
		// Nothing to do while this tab already holds a token. Nothing is claimed behind the user's back
		// while the lock is contended or was lost either — those are states the banner asks them to act on,
		// and re-claiming silently would either steal the lock back or bury the "not saved" warning.
		if (lockTokenRef.current !== null || lockState !== 'unlocked' || lockClaimInFlightRef.current) {
			return;
		}
		lockClaimInFlightRef.current = true;
		const generation = beginLockClaim();
		/** Nothing was proven, so nothing is recorded — but an unclaimed lock is exactly the failure this
		 * effect exists to prevent (no lock, no heartbeat, and a second editor's `beginEdit` then succeeds),
		 * and no dependency of this effect changes when an acquire fails: the report id is the same and
		 * `lockState` is still 'unlocked', so nothing would ever retry on its own. Bumping the attempt is
		 * what re-runs it, on a delay so a failing lock is retried rather than spun on, and bounded so a
		 * report whose lock genuinely cannot be claimed stops asking. */
		const retryAfterFailedClaim = (): void => {
			lockClaimInFlightRef.current = false;
			if (lockClaimRetriesRef.current >= LOCK_CLAIM_RETRY_LIMIT) {
				return;
			}
			lockClaimRetriesRef.current += 1;
			lockClaimRetryTimerRef.current = setTimeout(
				() => setLockClaimAttempt(attempt => attempt + 1),
				LOCK_CLAIM_RETRY_DELAY_MS
			);
		};
		// Bounded like every other lock claim: an `acquireLock` that never settles would otherwise latch
		// `lockClaimInFlightRef` for the rest of the session, after which every later claim — this effect's and
		// `beginEdit`'s alike — silently no-ops.
		void withLifecycleTimeout(dataSourceRef.current.acquireLock(savedReportIdForLockEffect)).then(bounded => {
			if (bounded.timedOut) {
				// Whether the lock was granted is unknown, so nothing is recorded; the server's TTL cleans up an
				// acquire that lands late. Retried on the same budget as an outright failure.
				retryAfterFailedClaim();
				return;
			}
			lockClaimInFlightRef.current = false;
			// Applied even if this effect has since been cleaned up: a granted token this tab drops is a
			// lock nothing releases until the server's TTL runs out. `applyLockClaim`'s generation check is
			// what discards a genuinely superseded claim.
			applyLockClaim(bounded.value, generation);
		}, retryAfterFailedClaim);
	}, [role, mode, savedReportIdForLockEffect, lockState, lockClaimAttempt, applyLockClaim, beginLockClaim]);

	/** One heartbeat tick: extends this tab's lock, or discovers it was lost. Reads the report id, the
	 * token AND the data source via refs (not closed-over state or props) so this callback's identity is
	 * stable for the life of the provider — `useLockHeartbeat` tears down and restarts its interval
	 * whenever `beat`'s identity changes, and a caller that hands the provider a fresh data source per
	 * render would restart the interval on every render, so no beat would ever survive long enough to
	 * fire. */
	const heartbeat = useCallback(() => {
		const reportId = savedReportIdForLockRef.current;
		const token = lockTokenRef.current;
		if (!reportId || !token) {
			return;
		}
		void dataSourceRef.current.heartbeatLock(reportId, token).then(
			claim => {
				// A stale response: something else (a takeover, a release) already changed which token, if
				// any, this tab holds since this beat was sent. Superseded — ignore it.
				if (lockTokenRef.current !== token) {
					return;
				}
				if (claim.token !== token) {
					// This tab's session is over, and this beat is how it finds out. A heartbeat either renews
					// the token it presented or is refused — report-service never mints a lock for a token that
					// matches nothing, because that would make the heartbeat endpoint an acquire that skips the
					// contention check. So a token that came back different, or not at all, means the lock this
					// tab held is gone: it lapsed and someone else has since claimed it.
					//
					// NEVER adopt `claim.token` — that would silently steal the lock back from whoever holds it.
					lockTokenRef.current = null;
					setLockState('lost');
					// Whoever holds the lock now may have stored something this tab cannot see.
					setDefinitionMayBeStale(true);
					setLockHolder(claim.status.holder);
					setLockedAt(claim.status.lockedAt);
				}
			},
			() => undefined // A transport failure just skips this beat; the next one retries.
		);
	}, []);

	// THE INVARIANT again, the "heartbeats it" half: keyed on holding the lock, NOT on being in edit mode.
	// A token held with `mode !== 'edit'` is reachable (a create-then-publish's automatic claim landing
	// after the top bar has already switched back to view — see the release effect above), and a
	// mode-keyed heartbeat left exactly those tokens to lapse while still recorded as held — blocking the
	// genuine holder for the server's full TTL.
	useLockHeartbeat({active: lockState === 'held', beat: heartbeat});

	/** One poll tick for a tab that is BLOCKED on someone else's lock: re-reads the lock and reconciles.
	 *
	 * Without this, a blocked viewer only ever learned the lock changed from the mount read or a pushed
	 * socket event — and a lock that lapses produces neither. The server's sweeper deletes the row
	 * silently (no holder released it, so nothing is announced), so a holder who closed their laptop,
	 * lost the network, or killed the tab left every other viewer showing "<name> is editing this
	 * report" until they happened to reload the page.
	 *
	 * Reads the report id and the data source through the same refs `heartbeat` does, and for the same
	 * reason: `useLockHeartbeat` restarts its interval whenever `beat`'s identity changes, so a callback
	 * rebuilt each render would never survive to fire. `reconcileLockStatus` goes through a ref for that
	 * same stability — it is a `useCallback` with dependencies, and closing over it directly would tie
	 * this callback's identity to theirs. */
	const pollBlockedLock = useCallback(() => {
		const reportId = savedReportIdForLockRef.current;
		if (!reportId) {
			return;
		}
		void dataSourceRef.current.lockStatus(reportId).then(
			status => {
				// Only a tab that is STILL blocked may apply this read. By the time it lands the tab may have
				// claimed the lock itself, so applying a status fetched before that would clobber a live claim.
				if (lockStateRef.current !== 'blocked') {
					return;
				}
				reconcileLockStatusRef.current(status);
			},
			() => undefined // A transport failure just skips this tick; the next one retries.
		);
	}, []);

	// The viewer half of the lock's liveness: a blocked tab polls so a lapsed lock stops being reported as
	// held. Deliberately the same interval the holder heartbeats on — one TTL is three ticks, so a viewer
	// learns a lock lapsed within a tick of the server letting it go, and a lock still being heartbeated
	// just reads back as held.
	useLockHeartbeat({active: lockState === 'blocked', beat: pollBlockedLock});

	const exportCsv = useCallback(async (): Promise<ExportOutcome> => {
		// Reentrancy guard, and deliberately a ref rather than the `exporting` state — same reasoning
		// as `save()`'s guard: two calls raised before a render commits would both see `exporting` as
		// false and both start a full-dataset export. A ref is set synchronously, so the second call is
		// refused whatever the render timing.
		//
		// Reported as 'failed' rather than as a distinct outcome: the first export is still running and
		// will report its own, and the busy control already says why this press did nothing.
		if (exportingRef.current) {
			return 'failed';
		}
		exportingRef.current = true;
		setExporting(true);
		try {
			// Commits an open, uncommitted rename first, for the same reason `save()` does: the exported
			// file's name comes from this definition, and an editor who typed a new name and clicked
			// Download without pressing the tick must get a file named for what is on screen, not the
			// last-committed name.
			const resolvedName = commitPendingName();
			// Serialised, not the raw working draft: column order, column widths and the sort column live
			// in `tableState` until a save folds them into the definition, so exporting `definition`
			// directly would hand the backend the last-saved (or agent-generated) layout and no sort at
			// all — a CSV whose columns and rows are ordered differently from the table the user is
			// looking at. `serializeDefinition` returns a new object, so the draft itself stays untouched
			// and an export does not count as a save.
			// Hidden columns travel separately, not folded into the serialised definition: `layout`
			// records order and width only, and `orderedDataColumnIds` deliberately re-appends any leaf
			// field the layout omits so an agent-generated subset still renders - which would put a
			// column the user just hid back into the file.
			await resolvedDataSource.exportCsv(
				serializeDefinition({...definition, name: resolvedName}, tableState, grouping),
				grouping,
				hiddenColumnPaths(tableState),
				taxonomy,
				formatLabelRef.current
			);
			return 'exported';
		} catch (error) {
			// Deliberately swallowed, mirroring `save()`: re-throwing would surface as an unhandled
			// rejection in any caller that does not chain a catch, which is what tore the surface down
			// before. The one failure worth distinguishing is the row ceiling, which no retry can get past.
			return error instanceof ReportExportTooLargeError ? 'too_large' : 'failed';
		} finally {
			exportingRef.current = false;
			setExporting(false);
		}
		// `taxonomy` belongs here: it resolves asynchronously, so a callback created before it lands
		// closes over null and the file's headings fall back to raw field paths - which is what the
		// export did until this dependency was added.
	}, [definition, tableState, grouping, resolvedDataSource, commitPendingName, taxonomy]);

	/** Checks whether the stored report actually changed while the edit lock was somewhere else, and settles
	 * the stale-view statement on the answer. Called on the success path of every way back into an edit
	 * session (entering edit mode, and re-claiming the lock from 'blocked' or 'lost') — that is the moment
	 * the editor resumes work they could otherwise publish over someone else's.
	 *
	 * Holding the lock is not editing. Another user can take the lock, change nothing, and hand it back, and
	 * that is the common case: they looked, or they were nudged off it. Treating displacement alone as proof
	 * that storage moved on leaves an editor with unsaved work unable to publish, with a reload — which
	 * destroys that work — as the only offer. So the stored definition is READ and compared against the last
	 * version this tab knew report-service had, and the three answers are treated differently:
	 *
	 * - unchanged: nothing was stored while the lock was away, so the statement stops being true and
	 *   publishing is allowed again. The working copy is left exactly as it is, unsaved edits and all —
	 *   there is nothing to replace it with that it does not already match.
	 * - changed with unsaved work: the statement stands (so `publish()` keeps refusing) and the working copy
	 *   stays on screen, which is the guarantee the whole displacement path exists for. Saving over the
	 *   stored version deliberately, or discarding, are the ways on.
	 * - changed with nothing unsaved: the stored definition is adopted, table state included, and the
	 *   statement stops being true.
	 *
	 * A read that fails or never settles changes NOTHING — not the definition, not the table, not the flag —
	 * so a transient failure leaves the surface exactly as stale as it already said it was rather than
	 * half-updated or falsely current. Never rejects: every caller reaches it after taking a lock, and a
	 * throw there would surface as an unhandled rejection.
	 *
	 * Table state is re-derived through `canonicalTableState` with hidden-ness derived, the same way the
	 * post-discard revert does it and for the same reason: this definition came back from storage, so a leaf
	 * field its layout omits is a column the user hid. */
	const reconcileStaleDefinition = useCallback(async (reportId: string) => {
		// Either flag means the same thing for this read: storage may have moved on from what is on screen.
		// `storedReportMayHaveChanged` belongs to a tab that only watched, and pressing Edit is exactly when
		// that stops being harmless — without it here, a viewer who saw the reload notice and clicked Edit
		// would resume from the copy that predates what the other editor published, and publish over it.
		if (!definitionMayBeStaleRef.current && !storedReportMayHaveChangedRef.current) {
			return;
		}
		let loaded: LoadedReport;
		try {
			// Bounded like every other lifecycle round trip: an unbounded read here would leave the editor
			// waiting on a call whose outcome nothing reports.
			const bounded = await withLifecycleTimeout(dataSourceRef.current.loadDefinition(reportId));
			if (bounded.timedOut) {
				return;
			}
			loaded = bounded.value;
		} catch {
			return;
		}
		// Adopted whatever the comparison below says, for the same reason the post-discard revert adopts it:
		// every update sends `privateAccess` and the backend overwrites it unconditionally, so the next save
		// has to echo the value this read just proved rather than the one the session started with. A null
		// carries no truthful value, so the known one is kept.
		const loadedPrivateAccess = loaded.privateAccess;
		if (typeof loadedPrivateAccess === 'boolean') {
			setSavedReport(prev => (prev === null ? prev : {...prev, privateAccess: loadedPrivateAccess}));
		}
		const stored = loaded.definition;
		if (definitionSignature(stored) === definitionSignature(storedDefinitionRef.current)) {
			// Nobody stored anything while the lock was elsewhere. Nothing on screen is touched — an unsaved
			// working copy is no less publishable than it was before someone else briefly held the lock.
			storedDefinitionRef.current = stored;
			setDefinitionMayBeStale(false);
			setStoredReportMayHaveChanged(false);
			return;
		}
		if (dirtyRef.current) {
			// Genuinely out of step, and there is unsaved work a replacement would destroy. The statement stands
			// and the copy stays. The baseline is deliberately NOT moved to what was just read: moving it would
			// make the next reconcile find no change and clear a statement that is still true.
			return;
		}
		setDefinition(stored);
		setGroupingState((stored.grouping ?? []) as GroupingToken[]);
		setTableState(canonicalTableState(stored, true));
		storedDefinitionRef.current = stored;
		// What is on screen is now what is stored, so the statement stops being true.
		setDefinitionMayBeStale(false);
		setStoredReportMayHaveChanged(false);
	}, []);

	// `heartbeat` calls this through `reconcileStaleDefinitionRef` (declared earlier, near the other lock
	// refs, for the same reason `reconcileLockStatusRef` exists): `heartbeat` has an empty dependency
	// array, so its closure is fixed before this callback even exists in that render's scope.
	reconcileStaleDefinitionRef.current = reconcileStaleDefinition;

	/** Opens the edit session before handing the surface over to edit mode. A report that has never
	 * been saved has no report row to lock or draft, so it goes straight to edit; a draft call that
	 * fails still enters edit mode, because the alternative is an editor stuck in view mode. No-ops
	 * (stays wherever it was) when another lifecycle call is already in flight, rather than racing it.
	 *
	 * The edit lock is acquired FIRST and is the only gate: report-service does not lock-check draft
	 * entry itself. A contended lock records the holder, sets `lockState` to 'blocked', and returns
	 * without opening a draft or touching `mode` — entering edit mode anyway would let this tab's local
	 * edits diverge from the session the other editor actually holds. An acquire call whose bounded wait
	 * expired is treated the same way (no draft, no edit mode): whether this tab holds the lock is then
	 * unknown, and entering edit mode on an unproven lock is worse than staying put.
	 *
	 * Once the lock is confirmed held, a draft call that never settles is neither the success nor the
	 * failure path: the draft may have opened server-side, so the wait is bounded, the guard is
	 * released, and the surface is marked out of step instead of picking a lifecycle state. Falling back
	 * to PUBLISHED/UNPUBLISHED there would leave Discard disabled on a draft that may well exist. Out of
	 * step says only what is true: the editor is in edit mode, nothing about the stored report can be
	 * confirmed, and the page has to be reloaded. */
	const beginEdit = useCallback(async () => {
		if (lifecycleInFlightRef.current) {
			return;
		}
		// Sorting, resizing and reordering in view mode is a viewing gesture and deliberately does not
		// mark the report changed. But the table state it produces is the state a later save writes, so
		// entering edit mode with the table arranged differently from the stored report means the next
		// publish would carry that arrangement. Say so on the way in: the editor sees the unsaved-changes
		// pill and can discard, rather than publishing a rearrangement nothing ever told them about.
		//
		// Computed here, applied only where `mode` actually becomes 'edit' below. Setting it up front used
		// to leave `dirty === true` with `mode` still 'view' whenever the lock could not be granted (a
		// contended lock, an acquire timeout, a transport failure) — the pill is hidden outside edit mode,
		// so nothing explained the state, `LeaveBuilderPrompt` blocked every in-app navigation with a
		// "you'll lose changes" modal, and only a page reload cleared it.
		const arrangementChangedSinceStored = !isSameTableState(
			tableState,
			canonicalTableState(definition, layoutRecordsVisibility)
		);
		lifecycleInFlightRef.current = true;
		try {
			const reportId = savedReport?.id;
			if (!reportId) {
				if (arrangementChangedSinceStored) {
					setDirty(true);
				}
				setMode('edit');
				return;
			}
			// Bounded like every other lifecycle round trip. The two bounded waits in this call are
			// SEQUENTIAL, not concurrent — the draft-entry wait below only starts once this one has settled —
			// so each timer measures exactly one call. Leaving the acquire unbounded latched
			// `lifecycleInFlightRef` for the rest of the session on a request that never settles, which
			// disables Edit, Publish and Discard with no spinner and no message.
			let claim: LockClaim;
			const claimGeneration = beginLockClaim();
			try {
				const boundedClaim = await withLifecycleTimeout(resolvedDataSource.acquireLock(reportId));
				if (boundedClaim.timedOut) {
					// Whether this tab holds the lock is unknown. Edit mode is NOT entered: an unproven lock is
					// exactly what must not be edited against, and the acquire may still land server-side, where
					// the lock's own TTL cleans it up.
					return;
				}
				claim = boundedClaim.value;
			} catch {
				// Transport failure: this tab's lock state is unproven, so it stays wherever it was and
				// edit mode is not entered.
				return;
			}
			applyLockClaim(claim, claimGeneration);
			if (!claim.token) {
				return;
			}
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.enterDraft(reportId));
				if (bounded.timedOut) {
					supersedeLifecycleCall();
					setReloadRequired(true);
					return;
				}
				const state = bounded.value;
				setDraftState(state.draftState ?? 'UNPUBLISHED');
				setPublishedAt(state.publishedAt);
				setDraftFilterWillRevert(state.draftFilterWillRevert);
			} catch {
				// Failing to open a draft means no draft was opened — the report's lifecycle state is
				// unchanged. A known publish date means it is still published; only fall back to
				// `UNPUBLISHED` when there is no publish date to fall back to. Never `DRAFT`: the
				// surface must not claim reader isolation it did not get.
				setDraftState(publishedAt !== null ? 'PUBLISHED' : 'UNPUBLISHED');
			} finally {
				if (arrangementChangedSinceStored) {
					setDirty(true);
				}
				setMode('edit');
			}
			// Re-entering edit while the view is known to be possibly out of date is exactly when a displaced
			// editor would otherwise resume from a copy that predates what the other editor published — so this
			// is where the question gets settled by reading storage rather than assumed. A no-op when the view
			// was never in doubt.
			await reconcileStaleDefinition(reportId);
		} finally {
			endLifecycleCall();
		}
		// `tableState`, `definition` and `layoutRecordsVisibility` are dependencies because the arrangement
		// check above reads them: without them this callback keeps the arrangement as it was when edit mode
		// was last available, and a rearrangement made since would go unflagged.
	}, [
		resolvedDataSource,
		savedReport,
		publishedAt,
		supersedeLifecycleCall,
		applyLockClaim,
		beginLockClaim,
		reconcileStaleDefinition,
		endLifecycleCall,
		tableState,
		definition,
		layoutRecordsVisibility,
	]);

	/* There is deliberately no in-place lock retry. A 'lost' session is recovered by reloading the page
	 * (see `LockStateBanner`): re-acquiring the lock in place would leave a working copy on screen that
	 * may already be behind what another editor stored, and only a fresh load is guaranteed to show what
	 * is stored. The staleness verification that a retry used to trigger still runs — `beginEdit` calls
	 * `reconcileStaleDefinition` on the way back into edit mode, so a newly acquired lock is still checked
	 * against the stored definition rather than assumed clean. */

	/** Saves the working copy and then makes it the version readers execute. Resolves 'published' on
	 * success, 'locked' when another user holds the edit lock, 'failed' for a failed save or publish, and
	 * 'unconfirmed' when either leg's bounded wait expired — which claims nothing either way, because the
	 * request may still land. Never rejects, for the same reason `save()` does not. */
	const publish = useCallback(async (): Promise<PublishOutcome> => {
		if (lifecycleInFlightRef.current) {
			return 'failed';
		}
		if (lockState === 'lost' || lockState === 'blocked') {
			// This tab does not hold the edit lock. Refused before touching `save()` or `dirty` — the unsaved
			// edits are not thrown away, only kept from being published over whoever holds it now. Reported as
			// 'locked' rather than 'failed': the surface's copy for a failure says nothing about why, and the
			// cause here is known and actionable, so the lock-specific message is the honest one.
			return 'locked';
		}
		if (definitionMayBeStale) {
			// Another editor is holding the lock, or held it and stored something this working copy does not
			// include — and a publish writes the working copy over the version
			// readers execute, which would silently destroy their published work. Refused before touching
			// `save()`, `dirty` or the definition: nothing on screen is thrown away, it just cannot be handed to
			// readers until the editor either reloads or discards. Its own outcome rather than 'locked': this tab
			// may well hold the lock, and the remedy is different.
			return 'stale';
		}
		if (savingRef.current) {
			// A save is still in flight, and `save()` refuses a second one, so this publish would save nothing
			// and could only report a failure that never happened. The in-flight save may still land, so the
			// stored report cannot be spoken for: gate the surface rather than telling the editor to try again.
			// Reached only if a save is triggered outside a publish, since a publish holds the guard above for
			// its whole run.
			setReloadRequired(true);
			return 'unconfirmed';
		}
		lifecycleInFlightRef.current = true;
		setPublishing(true);
		try {
			// The save is bounded too: `publish()` awaits it, so a save that never settles would latch this
			// call's guard exactly as a hung publish would. Superseding it also stops the save from adopting
			// its definition once it finally lands.
			const boundedSave = await withLifecycleTimeout(save());
			if (boundedSave.timedOut) {
				supersedeLifecycleCall();
				setReloadRequired(true);
				return 'unconfirmed';
			}
			if (!boundedSave.value) {
				// Nothing was saved, so nothing is owed: `save()` leaves the draft dirty, which keeps Publish
				// enabled on its own.
				return 'failed';
			}
			const reportId = savedReportIdRef.current;
			if (!reportId) {
				setPublishOwed(true);
				return 'failed';
			}
			const bounded = await withLifecycleTimeout(resolvedDataSource.publish(reportId, lockTokenRef.current));
			if (bounded.timedOut) {
				// The working copy is saved and the publish may or may not have landed, so neither the debt nor
				// the lifecycle state can be stated: gate the surface and let the editor reload the page to find out.
				supersedeLifecycleCall();
				setReloadRequired(true);
				return 'unconfirmed';
			}
			const state = bounded.value;
			if (state.locked) {
				// The save landed but the publish did not, and the save cleared `dirty` — record the debt so the
				// retry the toast invites is not blocked by a disabled Publish button.
				setPublishOwed(true);
				// The server just proved this tab does not hold the lock, so the surface must stop claiming it
				// does before the next heartbeat gets around to noticing.
				readAndReconcileLock(reportId);
				return 'locked';
			}
			setDraftState(state.draftState ?? 'PUBLISHED');
			setPublishedAt(state.publishedAt);
			setDraftFilterWillRevert(state.draftFilterWillRevert);
			setPublishOwed(false);
			return 'published';
		} catch {
			// Reached only after `save()` resolved (it never rejects), so the working copy is saved and only
			// the publish is missing.
			setPublishOwed(true);
			return 'failed';
		} finally {
			endLifecycleCall();
			setPublishing(false);
		}
	}, [
		resolvedDataSource,
		save,
		supersedeLifecycleCall,
		endLifecycleCall,
		lockState,
		readAndReconcileLock,
		definitionMayBeStale,
	]);

	/** Drops the edit session and adopts the definition report-service reverted to, so what is on
	 * screen always matches what was stored.
	 *
	 * The discard and the follow-up re-read are two separate failures with two separate truths. A failed
	 * discard changed nothing, server-side or here. A failed re-read means the draft IS gone — readers are
	 * already back on the live working copy — and only the reverted definition is missing, so the
	 * lifecycle state the discard reported is applied BEFORE the re-read is attempted. Applying it
	 * afterwards left the banner promising reader isolation that no longer existed, on top of telling the
	 * user nothing had been discarded.
	 *
	 * Resolves 'discarded', 'locked', 'failed', 'reload_failed' or 'unconfirmed'; never rejects. */
	const discard = useCallback(async (): Promise<DiscardOutcome> => {
		const reportId = savedReport?.id;
		if (!reportId || lifecycleInFlightRef.current) {
			return 'failed';
		}
		if (lockState === 'lost' || lockState === 'blocked') {
			// This tab does not hold the edit lock. Refused before calling report-service — nothing is dropped,
			// and the unsaved working copy stays exactly as it is on screen. Reported as 'locked' for the same
			// reason `publish()` does: the cause is known, so the copy should name it.
			return 'locked';
		}
		lifecycleInFlightRef.current = true;
		setDiscarding(true);
		try {
			let state: ReportLifecycleState;
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.discard(reportId, lockTokenRef.current));
				if (bounded.timedOut) {
					// Whether the draft was dropped is unknown, so neither the lifecycle state nor the definition
					// on screen can be trusted from here on: gate the surface and let the editor reload the page.
					supersedeLifecycleCall();
					setReloadRequired(true);
					return 'unconfirmed';
				}
				state = bounded.value;
			} catch {
				// No draft was dropped, so nothing here changes either.
				return 'failed';
			}
			if (state.locked) {
				// Same as `publish()`: the refusal is the proof, so the banner must not wait on a heartbeat. The
				// working copy on screen is untouched, which is the point — nothing here clears `dirty`.
				readAndReconcileLock(reportId);
				return 'locked';
			}
			setDraftState(state.draftState ?? 'PUBLISHED');
			setPublishedAt(state.publishedAt);
			setDraftFilterWillRevert(state.draftFilterWillRevert);
			// The working copy the publish was owed for is gone, so the debt is gone with it.
			setPublishOwed(false);
			// The draft WAS just dropped, so an open, uncommitted rename goes with it too — discarding
			// everything else while quietly applying whatever was mid-edit in the name field would be
			// exactly the silent-apply-on-blur behaviour the rename design deliberately has no path for.
			setPendingName(null);
			try {
				const bounded = await withLifecycleTimeout(resolvedDataSource.loadDefinition(reportId));
				if (bounded.timedOut) {
					supersedeLifecycleCall();
					// The draft IS gone and the reverted definition never arrived, which is the `reload_failed`
					// situation reached by a re-read that hung rather than failed.
					setDirty(false);
					setReloadRequired(true);
					return 'reload_failed';
				}
				const reverted = bounded.value;
				// The stored sharing flag is adopted along with the definition: `buildUpdateSavedReportInput`
				// sends `privateAccess` on every update and report-service overwrites it unconditionally, so the
				// next save has to echo the value this re-read just proved rather than the one the session
				// started with, or it would silently flip the report between private and shared. A null carries
				// no truthful value, so the known one is kept.
				const revertedPrivateAccess = reverted.privateAccess;
				if (typeof revertedPrivateAccess === 'boolean') {
					setSavedReport(prev => (prev === null ? prev : {...prev, privateAccess: revertedPrivateAccess}));
				}
				setDefinition(reverted.definition);
				// This read proves what is stored, so it becomes the baseline a later stale-view reconcile
				// compares against.
				storedDefinitionRef.current = reverted.definition;
				setGroupingState((reverted.definition.grouping ?? []) as GroupingToken[]);
				// Re-derive tableState from the reverted definition too — otherwise column order/sizing/sort
				// stay whatever the editor left them as pre-discard, so the table would keep showing a layout
				// the adopted definition no longer describes.
				// Hidden-ness is re-derived here regardless of how the builder was opened: `reverted.definition`
				// came from `loadDefinition`, so its layout is one a save through this builder wrote, whatever the
				// mount definition's provenance was.
				setTableState(canonicalTableState(reverted.definition, true));
				setDirty(false);
				// Adopted from the re-read rather than assumed: the server owns this flag, and a discard is
				// the one path that can lower it, because it throws away the edits that raised it. Clearing
				// only the session half and trusting a stale mount value let the banner disagree with
				// storage until the next reload.
				setManuallyEditedStored(reverted.manuallyEdited === true);
				setManuallyEditedSession(false);
				setReloadRequired(false);
				// This re-read is the stored report, so the view and storage agree again — including over
				// whatever another editor published while they held the lock. Discarding is therefore one of the
				// two ways out of a stale view that does not need a page reload.
				setDefinitionMayBeStale(false);
				setStoredReportMayHaveChanged(false);
			} catch {
				// What is on screen is the pre-discard working copy, which report-service has already dropped.
				// `dirty` is cleared and the surface is gated: leaving `dirty` set kept Publish enabled, and
				// publishing from here would put back exactly the content the editor was just told was
				// discarded. Neither Publish nor Discard means anything until the page is reloaded.
				setDirty(false);
				setReloadRequired(true);
				return 'reload_failed';
			}
			return 'discarded';
		} finally {
			endLifecycleCall();
			setDiscarding(false);
		}
	}, [resolvedDataSource, savedReport, supersedeLifecycleCall, endLifecycleCall, lockState, readAndReconcileLock]);

	// Row ids with a persist call in flight. Guards against a rapid double-click firing two
	// overlapping mutations for the same row — the second click is ignored outright (not queued)
	// while the first is pending, since a queued second call's rollback would revert whatever the
	// first call's outcome left behind rather than its own prior state.
	const pendingFavoriteRowIdsRef = useRef<Set<string>>(new Set());

	const toggleFavorite = useCallback(
		(rowId: string) => {
			if (pendingFavoriteRowIdsRef.current.has(rowId)) {
				return;
			}
			const row = findRowById(rowsRef.current, rowId);
			// A row that no longer exists locally, a deliverable row (which has no backing task and
			// cannot be favourited), or a task/subtask row with no taskId (should not happen once the
			// server always forces it — defensive), gets zero effect. The UI never renders a favorite
			// toggle for a deliverable row (see useReportTableColumns.tsx), but guard here too since this
			// callback is reachable programmatically via the context value, not just through that
			// button's onClick.
			if (!row || row.type === 'deliverable' || !row.taskId) {
				return;
			}
			const taskId = row.taskId;
			const wasFavourite = row.favorite;
			const nextFavoured = !wasFavourite;
			pendingFavoriteRowIdsRef.current.add(rowId);
			setRows(prev => toggleFavoriteInRows(prev, rowId));
			resolvedDataSource
				.toggleFavorite(taskId, nextFavoured)
				.catch(() => {
					// Low-stakes, instantly-retryable action (unlike Save/Publish): revert the optimistic
					// flip and stop there. No toast — the reverted star is sufficient feedback, and the
					// user can just click it again. Restores the captured pre-click value explicitly
					// (rather than re-flipping the row's current value): an intervening refetch (e.g. an
					// edited filter) can replace `rows` with fresh server data while this mutation is
					// still in flight, so "current" may already differ from what the optimistic update
					// produced — flipping it again would then write the wrong state.
					setRows(prev => setFavoriteInRows(prev, rowId, wasFavourite));
				})
				.finally(() => {
					pendingFavoriteRowIdsRef.current.delete(rowId);
				});
		},
		[resolvedDataSource]
	);

	const addCondition = useCallback(
		(fieldKey: string) => {
			if (!taxonomy) {
				return;
			}
			const field = fieldByKey(taxonomy, fieldKey);
			// Skip a field with no `filterField`: a condition stores the filterField enum, so a null
			// one would create a `field: null` condition that crashes label/summary rendering. Such
			// fields (non-filterable derived booleans) are never offered by the editor, but guard here
			// too so no caller can inject one.
			// Bound to a local so the narrowing survives into the `setDefinition` updater below; a
			// property access narrowed on `field` does not carry into a closure.
			const filterField = field?.filterField;
			if (!field || !filterField) {
				return;
			}
			setDefinition(prev => ({
				...prev,
				filter: {
					...prev.filter,
					filterConditions: [
						...prev.filter.filterConditions,
						{
							// Conditions store the filterField enum (STATUS, PROJECT, ...), not the
							// taxonomy key the picker is keyed by; the editor passes the key, we store
							// the field's filterField so it round-trips through the report-service contract.
							field: filterField,
							operator: defaultOperatorForDataType() as Condition['operator'],
							values: [],
							context: field.context as Condition['context'],
						},
					],
				},
			}));
			setDirty(true);
			setManuallyEditedSession(true);
		},
		[taxonomy]
	);

	const addColumn = useCallback(
		(fieldKey: string) => {
			// Same guard as addCondition, for the same reason: the definition may only reference
			// fields report-service actually serves. `fields` is what the fetch requests verbatim, so
			// an unknown key would come back with no cell and render an N/A column forever.
			if (!taxonomy || !fieldByKey(taxonomy, fieldKey)) {
				return;
			}
			// ensureFieldPath returns the same array when the path is already present, which makes
			// re-adding a column a genuine no-op rather than a state change that marks the report
			// dirty and triggers a pointless refetch. Decided here, off the ref that tracks the
			// current definition, so the `setDefinition` updater below stays a pure function of its
			// argument — React may run an updater more than once, so no side effect belongs inside it.
			if (ensureFieldPath(latestDefinitionRef.current.fields, fieldKey) === latestDefinitionRef.current.fields) {
				return;
			}
			setDefinition(prev => {
				const fields = ensureFieldPath(prev.fields, fieldKey);
				if (fields === prev.fields) {
					return prev;
				}
				// `fields` is a non-empty tuple on the wire type (the schema's minItems: 1), which
				// ensureFieldPath's plain array return cannot prove. It is non-empty by construction:
				// prev.fields already was, and ensureFieldPath only ever adds. Same cast idiom
				// serializeDefinition uses for `grouping`.
				return {...prev, fields: fields as ReportDefinitionV1['fields']};
			});
			// `columnOrder` is not derived from the definition after mount, so a field added here has
			// to be put into it explicitly. Left out, the table falls back to TanStack's "columns the
			// order does not mention go last", which puts the new column to the RIGHT of the fixed
			// favourite column; dragging it does nothing (`computeReorderedColumns` cannot find an id
			// the order lacks); and `serializeDefinition` builds `layout` from `columnOrder`, so a
			// save drops the column's position and width. Insert it where the canonical order would
			// have it: after the data columns, before the trailing favourite column.
			setTableState(prev => {
				if (prev.columnOrder.includes(fieldKey)) {
					return prev;
				}
				const favoriteIndex = prev.columnOrder.indexOf(FAVORITE_COLUMN_ID);
				const insertAt = favoriteIndex === -1 ? prev.columnOrder.length : favoriteIndex;
				const columnOrder = [...prev.columnOrder];
				columnOrder.splice(insertAt, 0, fieldKey);
				return {...prev, columnOrder};
			});
			setDirty(true);
		},
		[taxonomy]
	);

	const updateConditionValues = useCallback((index: number, values: string[], operator?: string) => {
		setDefinition(prev => {
			const filterConditions = prev.filter.filterConditions.map((condition, i) =>
				i === index
					? {
							...condition,
							values,
							// An explicit operator comes from a control that owns the whole comparison
							// (Remaining work's Yes/No). Otherwise keep inferring it from how many values
							// were picked, which is what every id-backed picker relies on.
							operator: (operator ?? operatorForValues(condition.operator, values)) as Condition['operator'],
					  }
					: condition
			);
			return {...prev, filter: {...prev.filter, filterConditions}};
		});
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const removeCondition = useCallback((index: number) => {
		setDefinition(prev => ({
			...prev,
			filter: {
				...prev.filter,
				filterConditions: prev.filter.filterConditions.filter((_, i) => i !== index),
			},
		}));
		setDirty(true);
		setManuallyEditedSession(true);
	}, []);

	const value: ReportBuilderContextValue = {
		definition,
		prompt: payload.prompt,
		// The only way to reach the builder without a stored record is the chat intent, which the
		// builder itself produced, so an absent value defaults to 'AI'. The re-open path always
		// supplies an explicit value.
		source: payload.source ?? 'AI',
		manuallyEdited,
		mode,
		role,
		dirty,
		tableState,
		grouping,
		taxonomy,
		setMode,
		setName,
		pendingName,
		setPendingName,
		commitPendingName,
		period: periodOf(definition),
		setPeriod,
		setPeriodPreset,
		setPeriodScope,
		setGrouping,
		updateTableState,
		save,
		savedReportId: savedReport?.id ?? null,
		openedFromStorage,
		updateSavedReportPrivateAccess,
		saving,
		exportCsv,
		exporting,
		nameValid: definition.name.trim() !== '',
		toggleFavorite,
		addCondition,
		addColumn,
		updateConditionValues,
		removeCondition,
		rows,
		totalCount,
		capped,
		cappedTotalEstimate,
		totalIsFloor,
		hasFetched,
		fetchFailed,
		fetchAccessDenied,
		retryFetch,
		draftState,
		publishedAt,
		draftFilterWillRevert,
		publishing,
		discarding,
		publishOwed,
		reloadRequired,
		definitionMayBeStale,
		storedReportMayHaveChanged,
		lockState,
		lockHolder,
		lockedAt,
		beginEdit,
		publish,
		discard,
	};

	return <ReportBuilderContext.Provider value={value}>{children}</ReportBuilderContext.Provider>;
}

export function useReportBuilder(): ReportBuilderContextValue {
	const ctx = useContext(ReportBuilderContext);
	if (!ctx) {
		throw new Error('useReportBuilder must be used within a ReportBuilderProvider');
	}
	return ctx;
}
